/* app/research-topics.jsx - Topics Bank, wired to /api/{topics,keywords,pages,faqs}
   per tab (read, add, edit, set status, delete), scoped to the active site. "Plan"
   promotes a row into a content plan, which is the edge from this bank to the
   Planner: it previews the deterministic assembly first
   (GET /content-plans/assemble-preview) and then creates the plan and its first
   piece in one write (POST /content-plans with `assemble`). "Classify" runs
   POST /api/classify/topics over the rows the button counts.

   This screen is the site's REGISTER, not an inbox of what Research captured.
   It holds every item the site has: published, in progress, planned, untouched.
   Two independent questions are asked of every row and they are never merged:

     status   - where the item sits in MarketLens's own pipeline
     coverage - whether the live site actually publishes something that says it

   Only the first one used to exist, so a site with 200 published articles read
   as entirely untouched, and the register invited the user to commission work
   the site already had. Coverage comes from GET /api/coverage (see
   backend/lib/coverage.js), which is filled by a scan of the live site.

   A third question is asked of the two banks whose table can answer it (topics
   and keywords): FIT, whether the row belongs to this business at all. The
   classifier has judged it all along and this screen showed none of it, so an
   imported half belonging to somebody else's market sat in the register looking
   like work to do. MARK AND FILTER, NEVER DELETE: off-market and off-topic rows
   are hidden by default behind a filter that says how many it is hiding, the
   rows stay, and any verdict can be overruled on the row (POST /classify/fit).

   "Grow topics" is the way the register gets WIDER: one seed the site already
   owns (a keyword, a WordPress category, a tag) goes to
   POST /api/research/topics/grow, which proposes further topics and writes
   nothing. What the user keeps is created through the ordinary create route,
   so the tree rules are enforced in one place. */
import { api } from '../lib/api.js';
import { newRunId, pollRun } from '../lib/run.js';
(function () {
  const { useState, useEffect, useCallback, useRef } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }
  const FD = (p) => React.createElement(window.MlBiHelpers.FacetDropdown, p);

  const arrOf = (v) => (Array.isArray(v) ? v : (v ? [v] : []));
  const capOf = (s) => (s ? s[0].toUpperCase() + s.slice(1) : "");
  // Every bank stores its own status vocabulary; the prototype shows one set of
  // four. Read direction (db → new/planned/used/archived). A page that is
  // "drafted" reads as planned: there is no fifth prototype state for it.
  const TB_IN = {
    topic:   { pending: "new", ready: "planned", in_use: "used", archived: "archived" },
    keyword: { raw: "new", classified: "planned", in_use: "used", archived: "archived" },
    page:    { idea: "new", planned: "planned", drafted: "planned", published: "used", archived: "archived" },
    faq:     { idea: "new", approved: "planned", archived: "archived" },
  };
  // Write direction. A missing key means the bank has no such status (a FAQ is
  // never "used"), so that option is left out of the bulk dropdown rather than
  // written as something it is not.
  //
  // "used" is deliberately absent for topics and keywords even though their
  // tables hold in_use: that status is DERIVED from plan edges
  // (backend/lib/plan-ingredients.js recomputes it and releases rows no live
  // plan references), so a hand-set "used" would be quietly reversed on the
  // next sync. Offering it would be offering a write the system undoes. A
  // page's "used" (published) is a real workflow state and stays.
  const TB_OUT = {
    topic:   { new: "pending", planned: "ready", archived: "archived" },
    keyword: { new: "raw", planned: "classified", archived: "archived" },
    page:    { new: "idea", planned: "planned", used: "published", archived: "archived" },
    faq:     { new: "idea", planned: "approved", archived: "archived" },
  };
  const statusIn = (type, s) => (TB_IN[type] || {})[s] || "new";
  // topics/pages/faqs live on the research router (/api/research/*); keywords is top-level.
  const ENDPOINT = { topic: "research/topics", keyword: "keywords", page: "research/pages", faq: "research/faqs" };
  // The entity's NAME rides along when the server sends it, so the row can say
  // "via competitor Acme" rather than leaving the user to guess which one.
  const fromOf = (r) => (r && r.source_entity_type
    ? "via " + r.source_entity_type + (r.source_name ? " " + r.source_name : "")
    : null);
  const whenOf = (r) => (r && r.created_at ? String(r.created_at).slice(0, 10) : "");
  // The two banks whose table carries a fit verdict. Pages and FAQs have no
  // such column, so the Fit control is absent on those tabs entirely rather
  // than rendering every row as "not judged", which would be a statement about
  // a question nobody ever asked of them.
  const FIT_BANKS = ["topic", "keyword"];
  // null fit means nothing has ever judged this row, which is NOT the same as
  // 'unclear'. One is waiting for a run, the other has been looked at and is
  // waiting for a person, and they are never merged on this screen. Who decided
  // is its own column (fit_source), never a prefix on the reason: that field is
  // free text a user can type into.
  const fitOf = (r) => ({ fit: r.fit || null, fitWhy: r.fit_reason || null, fitByHand: r.fit_source === "human" });
  function mapItem(type, r) {
    if (type === "keyword") return { id: r.id, title: r.keyword || "", status: statusIn(type, r.status), tags: [capOf(r.intent)].filter(Boolean), detail: r.cluster || "", from: fromOf(r), when: whenOf(r), ...fitOf(r) };
    if (type === "faq") return { id: r.id, title: r.question || r.title || "", status: statusIn(type, r.status), tags: ["FAQ"], detail: r.answer || r.detail || "", from: fromOf(r), when: whenOf(r) };
    // parent_id is the topics bank's tree edge (a theme, and the articles under
    // it). No other bank has the column, so it reads as null there and every
    // tab but Topics goes on rendering a flat list. fit is spread in for both,
    // and is simply undefined on a pages row, which the Fit control never asks
    // about because pages are not one of FIT_BANKS.
    // intent, format and category are what a classify run writes, and they were
    // stored invisibly here for as long as the run existed: a user paid tokens
    // for facets the register never showed. They are surfaced on the row and
    // kept apart from `tags`, because a facet and a tag are different claims.
    // pageType exists only on a pages row (content_pages has no facet columns),
    // and reads as null on a topic, where nothing asks for it.
    return { id: r.id, title: r.title || "", status: statusIn(type, r.status), tags: arrOf(r.tags), detail: r.detail || r.summary || "", from: fromOf(r), when: whenOf(r), parent_id: r.parent_id == null ? null : Number(r.parent_id), intent: r.intent || null, format: r.format || null, category: r.category || null, pageType: r.page_type || null, ...fitOf(r) };  // topic | page
  }

  // ── Fit: does a row belong to this business at all ────────
  //
  // FIVE states, not four. The classifier reaches one of four verdicts; the
  // fifth is a row nothing has judged, which the column stores as NULL and
  // which this screen calls "not judged". Collapsing it into "unclear" would
  // put rows nobody asked a question about into the queue a person works
  // through.
  //
  // off_market and off_topic are kept apart everywhere, because they lead to
  // different actions: an off-market topic is the right subject in the wrong
  // place, an off-topic one is a different business entirely (usually a bad
  // import). Both are hidden by default and NEITHER is ever deleted.
  const FIT_LABEL = { relevant: "Relevant", off_market: "Off market", off_topic: "Off topic", unclear: "Unclear" };
  const FIT_COLOR = {
    relevant: "var(--success)", off_market: "var(--warning)", off_topic: "var(--danger)",
    unclear: "var(--info)", none: "var(--text-muted)",
  };
  // The two the default view hides. Named once, so the filter, the count of
  // what it hid and the sentence explaining it cannot drift apart.
  const HIDDEN_FITS = ["off_market", "off_topic"];
  const FIT_OPTS = [
    ["relevant", "Relevant"], ["unclear", "Unclear"],
    ["off_market", "Off market"], ["off_topic", "Off topic"],
    ["none", "Not judged"],
  ];
  const FIT_VIEW_OPTS = [
    ["keep", "Fit: hiding off market and off topic"],
    ["all", "Fit: everything"],
    ["relevant", "Fit: relevant only"],
    ["unclear", "Fit: unclear only"],
    ["unjudged", "Fit: not judged yet"],
    ["off_market", "Fit: off market only"],
    ["off_topic", "Fit: off topic only"],
  ];
  // What the hover says, which is the only place the classifier's one sentence
  // is readable. A row nobody has judged says so rather than showing an empty
  // reason: "no verdict" and "a verdict with no reason" are different.
  function fitTitle(it, noun) {
    if (!it.fit) return `Nothing has judged whether this ${noun} belongs to this business. Classify it, or set a verdict here.`;
    const why = String(it.fitWhy || "").trim();
    return [
      FIT_LABEL[it.fit] || it.fit,
      it.fitByHand ? " (you set this, so a classify run will not overwrite it)" : "",
      why ? ": " + why : ". No reason was recorded.",
    ].join("");
  }
  // The default view keeps everything except the two verdicts that say "not
  // this business". Every other option is an explicit choice to look at one
  // state.
  function fitMatches(it, view) {
    switch (view) {
      case "all": return true;
      case "keep": return !HIDDEN_FITS.includes(it.fit);
      case "unjudged": return !it.fit;
      default: return it.fit === view;
    }
  }

  const TYPES = [["topic", "Topics", "layers"], ["keyword", "Keywords", "target"], ["page", "Pages", "file-text"], ["faq", "FAQ", "help-circle"]];
  const TB_STATUS = [["new", "New"], ["planned", "Planned"], ["used", "Used"], ["archived", "Archived"]];
  const TB_STATUS_COLOR = { new: "var(--warning)", planned: "var(--blue-500)", used: "var(--success)", archived: "var(--text-muted)" };

  function statusTag(s) {
    const { Badge } = window.HuddleDesignSystem_775113;
    const map = { new: ["warning", "New"], planned: ["info", "Planned"], used: ["success", "Used"], archived: ["neutral", "Archived"] };
    const [tone, l] = map[s];
    return <Badge tone={tone} dot={s !== "archived"}>{l}</Badge>;
  }

  // ── Coverage ──────────────────────────────────────────────
  // The bank tabs and the coverage kinds are the same four words, which is why
  // the active tab can be passed straight through as ?kind=.
  //
  // What a match is WORTH is the part that must not be flattened. `manual`,
  // `pipeline` and `focus_keyword` are records: a person said so, we published
  // the piece ourselves, or the page itself declares the term. `title` and
  // `slug` are inferences, drawn from the item's full label appearing as a
  // phrase in a page, and the backend prices them at 70 and 60 for exactly that
  // reason. Presented identically, a guess would read as a fact and the user
  // would skip work the site does not actually have.
  const RULE_RANK = ["manual", "pipeline", "focus_keyword", "title", "slug"];
  const PROOF_RULES = ["manual", "pipeline", "focus_keyword"];
  const RULE_SAYS = {
    manual       : "marked by hand",
    pipeline     : "published from a plan here",
    focus_keyword: "the page's own focus keyword",
    title        : "the phrase is in the page title",
    slug         : "the phrase is in the page URL",
  };
  const isProof = (rule) => PROOF_RULES.includes(rule);
  const rankOf = (rule) => { const i = RULE_RANK.indexOf(rule); return i === -1 ? RULE_RANK.length : i; };
  const bestRule = (posts) => (posts || []).reduce((best, p) => (best === null || rankOf(p.matched_by) < rankOf(best) ? p.matched_by : best), null);

  // Six states, not two. Four of them are cases where the answer is not known,
  // and rendering any of those as "not covered" would be the false negative
  // this whole register exists to prevent.
  const COV_LOOK = {
    loading  : ["Checking coverage", "bi-badge--archived",  "clock"],
    unscanned: ["Not scanned",       "bi-badge--archived",  "clock"],
    unknown  : ["Coverage unknown",  "bi-badge--archived",  "help-circle"],
    none     : ["Not covered",       "bi-badge--archived",  "circle"],
    covered  : ["Covered",           "bi-badge--validated", "check-circle"],
    likely   : ["Likely covered",    "bi-badge--look",      "help-circle"],
    duplicate: ["Duplicated",        "bi-badge--pending",   "alert-triangle"],
  };
  const COV_WHY = {
    loading  : "Reading the coverage map.",
    unscanned: "Nothing has read this site yet, so this is not a claim that the site is missing it.",
    unknown  : "The coverage map did not answer for this row, so neither answer can be given.",
    none     : "The last scan read the site and found no published page that says this.",
    covered  : "A published page covers this, on a record rather than a guess.",
    likely   : "A published page looks like it covers this, inferred from a phrase in its title or URL. Open it and check.",
    duplicate: "More than one published page covers this, which is possible duplication.",
  };
  const COV_FILTERS = [
    ["all",       "All",         "Every row, whatever its coverage."],
    ["covered",   "Covered",     "A published page covers it, whether that is a record or an inference."],
    ["none",      "Not covered", "The last scan read the site and found nothing that says it."],
    ["duplicate", "Duplicated",  "More than one published page covers it."],
  ];

  function covBadge(state, entry, why) {
    const [label, cls, icon] = COV_LOOK[state];
    const n = entry && entry.posts ? entry.posts.length : 0;
    return <span className={"bi-badge " + cls} title={why || COV_WHY[state]}><I name={icon} size={11} />{label}{state === "duplicate" ? ` (${n} pages)` : ""}</span>;
  }

  // One covering page, named and linked. The rule that found it is stated on
  // the row itself rather than only in a tooltip: "record" and "inferred 70%"
  // are different claims and the user is choosing whether to write again on the
  // strength of them. A pipeline match can carry no URL (the post may be
  // scheduled or private, so the scan never saw it), and a link to nowhere
  // would be a control that claims something it cannot do.
  function CoveringPost({ p }) {
    const proof = isProof(p.matched_by);
    const name = p.post_title || `Post #${p.post_id}`;
    const tag = proof
      ? <span className="bi-badge bi-badge--validated">record</span>
      : <span className="bi-badge bi-badge--look">inferred{p.confidence == null ? "" : ` ${p.confidence}%`}</span>;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {p.post_url
          ? <a className="bi-card__url" href={p.post_url} target="_blank" rel="noreferrer"><I name="external-link" size={13} />{name}</a>
          : <span className="bi-card__url" style={{ color: "var(--text-secondary)" }} title="The scan has no URL for this page, so there is nothing to open."><I name="file-text" size={13} />{name}</span>}
        {tag}
        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{RULE_SAYS[p.matched_by] || p.matched_by}</span>
      </div>
    );
  }

  // The scan, behind a modal because it is slow: it pages through every
  // published post and page on the live site over the REST API. The progress is
  // the server's own milestones (GET /api/runs/:id via lib/run.js), so a stalled
  // scan stops moving instead of looking healthy, and what it reports afterwards
  // is what came back, including the ways it fell short.
  function SyncCoverage({ siteId, onClose, onDone }) {
    const [phase, setPhase] = useState("idle");   // idle | running | done | failed
    const [run, setRun] = useState(null);
    const [res, setRes] = useState(null);
    const [err, setErr] = useState(null);
    const stop = useRef(null);
    useEffect(() => () => { if (typeof stop.current === "function") stop.current(); }, []);

    const start = async () => {
      if (siteId == null) { setErr("No active site."); setPhase("failed"); return; }
      setPhase("running"); setRun(null); setErr(null); setRes(null);
      const runId = newRunId();
      stop.current = pollRun(runId, setRun);
      try {
        const r = await api(`/coverage/sync`, { method: "POST", body: JSON.stringify({ run_id: runId, site_id: siteId }) });
        stop.current();
        setRes(r); setPhase("done");
        onDone && onDone();
      } catch (e) {
        // The endpoint writes its errors for the user (unreachable host, refused
        // credentials, no known site URL), so its message is shown as it came
        // rather than replaced with a generic failure.
        stop.current();
        setErr(e.message || "The coverage scan failed."); setPhase("failed");
      }
    };

    const foot = phase === "running" ? null
      : phase === "done" ? <button className="bi-btn bi-btn--primary" onClick={onClose}>Close</button>
      : <>
          <button className="bi-btn" onClick={onClose}>Cancel</button>
          <button className="bi-btn bi-btn--primary" onClick={start}><I name="globe" size={16} />{phase === "failed" ? "Try again" : "Read the site"}</button>
        </>;

    const c = (res && res.counts) || {};
    const br = (res && res.by_rule) || {};
    const proofRows = (br.manual || 0) + (br.pipeline || 0) + (br.focus_keyword || 0);
    const inferredRows = (br.title || 0) + (br.slug || 0);

    return React.createElement(window.MlBiModals._Modal, {
      title: "Sync coverage",
      sub: "Reads every published post and page on the site and matches them against the banks.",
      onClose: phase === "running" ? () => {} : onClose,
      foot,
    }, (
      <div className="bi-modal__body">
        {phase === "idle" && (
          <div className="bi-hint">
            Nothing here costs tokens: every match is an exact rule, not a model. The scan reads the site's REST API,
            so it takes as long as the site is large, and it can only see <b>published</b> posts and pages.
            Matches it can prove are replaced; anything marked by hand is left alone.
          </div>
        )}
        {phase === "running" && (
          <div className="bi-fetch-progress">
            <div className="ml-progress-row" style={{ marginBottom: 12 }}>
              <div className="ml-progress"><div className="ml-progress__bar" style={{ width: (run ? run.pct : 0) + "%" }} /></div>
              <span className="ml-progress-row__pct">{run ? run.pct : 0}%</span>
            </div>
            <div className="bi-fetch-step bi-fetch-step--active">
              <span className="bi-fetch-step__dot"><span className="ml-spinner ml-spinner--sm" style={{ width: 14, height: 14 }} /></span>
              <span className="bi-fetch-step__label">
                {run ? `Step ${Math.max(1, run.step)} of ${run.steps} · ${run.label}` : "Waiting for the server to start"}
                {run && run.note ? <span style={{ color: "var(--text-muted)" }}> · {run.note}</span> : null}
              </span>
            </div>
          </div>
        )}
        {phase === "failed" && <div className="bi-hint" style={{ color: "var(--danger)" }}>{err}</div>}
        {phase === "done" && res && (
          <>
            <div className="bi-card__meta">
              <div className="bi-meta-row"><span className="bi-meta-row__l">Pages read</span><span className="bi-meta-row__v">{res.scanned}{res.site_total ? ` of ${res.site_total} published on the site` : ""}{c.post != null ? ` (${c.post} posts, ${c.page} pages)` : ""}</span></div>
              <div className="bi-meta-row"><span className="bi-meta-row__l">Now covered</span><span className="bi-meta-row__v">{TYPES.map(([k, l]) => `${l.toLowerCase()} ${(res.matched || {})[k] || 0} of ${(res.bank_totals || {})[k] || 0}`).join(", ")}</span></div>
              <div className="bi-meta-row"><span className="bi-meta-row__l">On what</span><span className="bi-meta-row__v">{proofRows} match{proofRows === 1 ? "" : "es"} on a record, {inferredRows} inferred from a title or URL</span></div>
              <div className="bi-meta-row"><span className="bi-meta-row__l">Duplicated</span><span className="bi-meta-row__v">{res.duplicates_total || 0} item{(res.duplicates_total || 0) === 1 ? "" : "s"} covered by more than one page</span></div>
              <div className="bi-meta-row"><span className="bi-meta-row__l">Matched nothing</span><span className="bi-meta-row__v">{res.unmatched || 0} of the pages read say nothing this register holds</span></div>
              {res.manual_kept > 0 && <div className="bi-meta-row"><span className="bi-meta-row__l">Left alone</span><span className="bi-meta-row__v">{res.manual_kept} hand-marked row{res.manual_kept === 1 ? "" : "s"}</span></div>}
            </div>
            {res.capped && (
              <div className="bi-hint" style={{ marginTop: 12 }}>
                <I name="alert-triangle" size={14} style={{ color: "var(--warning)", verticalAlign: "-2px", marginRight: 6 }} />
                <b>The whole site was not read.</b> The scan stops at {res.cap} pages and this site has {res.site_total},
                so what it did not reach is missing from the map rather than absent from the site.
              </div>
            )}
            {res.note && !res.capped && <div className="bi-hint" style={{ marginTop: 12 }}>{res.note}</div>}
          </>
        )}
      </div>
    ));
  }

  const ADD_CFG = {
    topic:   { title: "Add topic",   main: "Title",    extra: "Detail", sub: "Something worth writing about. It joins the bank as new." },
    keyword: { title: "Add keyword", main: "Keyword",  extra: null,     sub: "A term this site should rank for. It joins the bank as new." },
    page:    { title: "Add page",    main: "Title",    extra: "Detail", sub: "A page the site is missing. It joins the bank as an idea." },
    faq:     { title: "Add FAQ",     main: "Question", extra: "Answer", sub: "A question buyers actually ask. It joins the bank as an idea." },
  };
  // The column is constrained to these, so the picker offers exactly them.
  const PAGE_KINDS = [["post", "Post"], ["blog", "Blog"], ["service", "Service"], ["landing", "Landing"], ["other", "Other"]];

  // The four intents a classify run writes for a topic, offered in the edit
  // form so a wrong verdict can be corrected where it is read. "Not set" is a
  // real choice: it clears the field (PATCH intent: null) rather than forcing
  // every edited topic to claim an intent somebody never decided.
  const TOPIC_INTENTS = [["", "Not set"], ["informational", "Informational"], ["commercial", "Commercial"], ["transactional", "Transactional"], ["navigational", "Navigational"]];

  // Manual add, and since the same fields are what an edit changes, the same
  // form edits. Topics, pages and FAQs each have a create route on the research
  // router; the keywords bank exposes only the bulk insert, so one keyword goes
  // through that with a single-item array.
  //
  // `parent` is the theme a new topic is being filed under, and only the Topics
  // bank ever passes one. It is named in the dialog rather than implied by
  // which button was pressed: a topic that quietly acquired a parent is a topic
  // the user then has to go looking for.
  //
  // `item` is the row being EDITED, and its presence is what flips the form:
  // the fields open pre-filled, and saving PATCHes only what actually changed,
  // so a field the user never touched is not stamped over with a stale copy. A
  // FAQ's question and answer travel as title and detail on the PATCH, which
  // is the update route's vocabulary for them.
  function AddItem({ type, siteId, parent, item, onClose, onAdded }) {
    const cfg = ADD_CFG[type];
    const editing = !!item;
    const [main, setMain] = useState(editing ? item.title : "");
    const [extra, setExtra] = useState(editing ? (item.detail || "") : "");
    const [pageKind, setPageKind] = useState(editing && item.pageType ? item.pageType : "post");
    const [intent, setIntent] = useState(editing && item.intent ? item.intent : "");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const save = async () => {
      const v = main.trim();
      if (!v) { setErr(cfg.main + " is required."); return; }
      if (siteId == null) { setErr("No active site."); return; }
      if (editing) {
        // Only the fields that differ from what the row held are sent, and a
        // save with nothing changed says so instead of issuing a write the
        // server would apply as a no-op the user believes did something.
        const patch = {};
        if (v !== item.title) patch.title = v;
        if (extra.trim() !== (item.detail || "")) patch.detail = extra.trim();
        if (type === "page" && pageKind !== (item.pageType || "post")) patch.page_type = pageKind;
        if (type === "topic" && intent !== (item.intent || "")) patch.intent = intent || null;
        if (!Object.keys(patch).length) { setErr("Nothing has changed, so nothing was sent."); return; }
        setBusy(true); setErr(null);
        try {
          await api(`/${ENDPOINT[type]}/${item.id}?site_id=${siteId}`, { method: "PATCH", body: JSON.stringify(patch) });
          onAdded(v);
        } catch (e) { setErr(e.message || "Could not save the changes."); setBusy(false); }
        return;
      }
      setBusy(true); setErr(null);
      try {
        if (type === "keyword") {
          const r = await api(`/keywords/bulk`, { method: "POST", body: JSON.stringify({ site_id: siteId, keywords: [{ keyword: v }] }) });
          // bulk reports what it stored; a skipped row is not an add.
          if (!r || !r.inserted) throw new Error("The keyword was not stored.");
        } else {
          const body = { site_id: siteId };
          if (type === "faq") { body.question = v; if (extra.trim()) body.answer = extra.trim(); }
          else { body.title = v; if (extra.trim()) body.detail = extra.trim(); if (type === "page") body.page_type = pageKind; }
          // The server owns the tree rules and refuses in words (a parent from
          // another site, a parent that is already a child), so a refusal
          // arrives as the message shown below rather than a silent root.
          if (parent) body.parent_id = parent.id;
          await api(`/${ENDPOINT[type]}`, { method: "POST", body: JSON.stringify(body) });
        }
        onAdded(v);
      } catch (e) { setErr(e.message || "Could not save it."); setBusy(false); }
    };

    return React.createElement(window.MlBiModals._Modal, {
      sm: true,
      title: editing ? cfg.title.replace("Add", "Edit") : (parent ? "Add child topic" : cfg.title),
      sub  : editing ? "Only what you change is sent; everything else on the row stays as it is."
           : parent ? `It joins the bank as new, filed under "${parent.title}".` : cfg.sub,
      onClose: busy ? () => {} : onClose,
      foot: <>
        <button className="bi-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="bi-btn bi-btn--primary" onClick={save} disabled={busy || !main.trim()}>{busy ? "Saving…" : (editing ? "Save changes" : (parent ? "Add child topic" : cfg.title))}</button>
      </>,
    }, (
      <div className="bi-modal__body" style={{ paddingTop: 4, paddingBottom: 8 }}>
        <label className="bi-field__label">{cfg.main} <span className="bi-field__req">*</span></label>
        <input className="hds-input" value={main} autoFocus onChange={e => setMain(e.target.value)} onKeyDown={e => { if (e.key === "Enter") save(); }} />
        {type === "page" && <>
          <label className="bi-field__label" style={{ marginTop: 12 }}>Page type</label>
          <select className="hds-select" value={pageKind} onChange={e => setPageKind(e.target.value)}>{PAGE_KINDS.map(([id, l]) => <option key={id} value={id}>{l}</option>)}</select>
        </>}
        {/* Intent is offered when EDITING a topic only: on add nothing has
            judged the row yet, and classify is what fills it in. The edit form
            is where a person corrects what a run decided. */}
        {editing && type === "topic" && <>
          <label className="bi-field__label" style={{ marginTop: 12 }}>Intent</label>
          <select className="hds-select" value={intent} onChange={e => setIntent(e.target.value)}>{TOPIC_INTENTS.map(([id, l]) => <option key={id} value={id}>{l}</option>)}</select>
        </>}
        {cfg.extra && <>
          <label className="bi-field__label" style={{ marginTop: 12 }}>{cfg.extra}</label>
          <textarea className="hds-textarea" rows={4} value={extra} onChange={e => setExtra(e.target.value)} placeholder="Optional" />
        </>}
        {err && <div className="bi-hint" style={{ color: "var(--danger)" }}>{err}</div>}
      </div>
    ));
  }

  // ── Growing the bank from a seed ──────────────────────────
  //
  // Capture fills this bank from an analysis, which is always about somebody
  // else: a competitor's page, a market, a partner. There was no way to say
  // "we already own this keyword / this category / this tag, give me more to
  // write in THAT area", which is how a site widens what it covers.
  //
  // A seed is one thing OR a group of keywords. One keyword is often too
  // small an ask, and the register's real structure is the clusters the
  // Classify run writes onto it, so a run can also be seeded from a whole
  // cluster or from keywords ticked by hand. A hand-picked row the server
  // will not take is refused by name in the server's own words, never
  // silently dropped: the user chose it, so a quiet drop would run a
  // different request than the one made.
  //
  // Three things this panel has to keep straight, because each of them is a way
  // to mislead the person deciding:
  //
  // WHERE THE SEEDS COME FROM. The site's own keyword register and its live
  // WordPress terms, read by GET /research/topics/grow/options. A source that
  // could not be read says so and still lets a seed be typed; it never renders
  // as a site with no categories, which is the same shape as an unreachable
  // one and the opposite conclusion.
  //
  // NOTHING IS WRITTEN UNTIL IT IS KEPT. The proposals are a list to review.
  // What the user ticks is created through the ordinary create route, one row
  // at a time, and a row the server refuses is named rather than folded into a
  // count.
  //
  // COVERAGE IS THE SITE'S OWN ANSWER. Each proposal carries what the last scan
  // could prove about it, and "not known" is a real answer that is never shown
  // as "not covered": a wrong "you already have this" costs the user an article
  // they need.
  const GROW_SEED_TYPES = [
    ["keyword", "Keyword", "target", "one this site's register already holds"],
    ["cluster", "Keyword cluster", "layers", "every keyword sharing one cluster, grown as a set"],
    ["keywords", "Pick keywords", "check-square", "several keywords picked by hand, grown as a set"],
    ["category", "Category", "folder", "one its WordPress really files under"],
    ["tag", "Tag", "tag", "one its WordPress really uses"],
  ];
  // The two types whose seed is a GROUP of keywords rather than one string.
  // They are register-bound: a cluster the register does not hold is refused
  // by the server (unlike a typed keyword, the whole point is the rows behind
  // the name), and a hand-picked set is sent as ids, so neither offers the
  // "type one instead" escape the single seeds keep.
  const GROUP_SEED = { cluster: true, keywords: true };
  // Which seeds a run accepts is the SERVER's list (grow/options sends
  // seed_types), and the hardcoded three above are only the fallback for a
  // reply that does not carry it: a seed type the server grew to accept must
  // not be invisible because this file predates it. A server id is matched to
  // the local icon and hint when it is one of the known three, and still
  // renders without them when it is not, because the list is the truth and the
  // decoration is not.
  function seedTypesOf(data) {
    const sent = data && Array.isArray(data.seed_types) ? data.seed_types : null;
    if (!sent || !sent.length) return GROW_SEED_TYPES;
    const out = [];
    for (const s of sent) {
      const id = typeof s === "string" ? s.trim() : String((s && (s.id || s.type)) || "").trim();
      if (!id) continue;
      const local = GROW_SEED_TYPES.find(t => t[0] === id);
      const label = (s && typeof s === "object" && s.label) || (local ? local[1] : capOf(id));
      const hint  = (s && typeof s === "object" && (s.hint || s.note)) || (local ? local[3] : "");
      out.push([id, label, local ? local[2] : "tag", hint]);
    }
    return out.length ? out : GROW_SEED_TYPES;
  }
  // The ladder "How many ideas" offers, cut to the server's ceiling. The run's
  // own default stays the server's: the empty choice sends no `want` at all.
  const WANT_LADDER = [3, 5, 8, 10, 12, 15, 20, 25, 30];
  // The three answers the server's coverage check can give, onto the badges
  // this screen already uses. A proposal has no history in the bank, so
  // "covered" is the whole finding rather than one of six states.
  const PROP_COV = { covered: "covered", no_match: "none", not_known: "unknown" };

  // The hand-picked seed's checklist: the same register rows the keyword seed
  // lists, each tickable, with a filter box on top. The filter narrows what is
  // SHOWN and never what is PICKED, and the count line says so when the two
  // disagree: a row ticked and then filtered out of view still counts, and a
  // selection that silently shrank because of a search box would misrepresent
  // the run about to be asked for.
  function KeywordMultiPick({ rows, picked, onToggle, query, onQuery }) {
    const q = query.trim().toLowerCase();
    const shown = q ? rows.filter(r => String(r.name || "").toLowerCase().includes(q)) : rows;
    const hiddenPicked = q ? picked.filter(id => !shown.some(r => r.id === id)).length : 0;
    return <>
      <input className="hds-input" value={query} onChange={e => onQuery(e.target.value)} placeholder={`Filter the ${plural(rows.length, "keyword", "keywords")}…`} />
      <div className="bi-checklist" style={{ maxHeight: 220, overflowY: "auto", marginTop: 8 }}>
        {shown.map(r => (
          <label className="bi-checkrow" key={r.id} onClick={() => onToggle(r.id)}>
            <span className="hds-check"><input type="checkbox" readOnly checked={picked.includes(r.id)} /><span className="hds-check__box"><I name="check" size={12} /></span></span>
            <span className="bi-checkrow__name">{r.name}</span>
          </label>
        ))}
        {shown.length === 0 && <div className="bi-subempty">No keyword matches "{query.trim()}".{picked.length ? ` The ${plural(picked.length, "row", "rows")} already ticked ${picked.length === 1 ? "stays" : "stay"} picked.` : ""}</div>}
      </div>
      <div className="bi-hint" style={{ marginTop: 6 }}>
        {picked.length
          ? <>{picked.length} of the {rows.length} picked{hiddenPicked ? <>, {hiddenPicked} of them hidden by the filter but still counted</> : null}. They are grown as one set.</>
          : "Tick at least one. The run proposes topics that serve the picked keywords as a set, not one topic per keyword."}
      </div>
    </>;
  }

  function GrowTopics({ siteId, parent, onClose, onAdded, onDepth }) {
    const [opts, setOpts] = useState({ loading: true, error: null, data: null });
    const [seedType, setSeedType] = useState("keyword");
    const [seed, setSeed] = useState("");
    const [typed, setTyped] = useState(false);      // the seed is being typed rather than picked
    const [seedIds, setSeedIds] = useState([]);     // the hand-picked rows, by id
    const [seedQuery, setSeedQuery] = useState(""); // narrows the checklist; never the selection
    // "" means the server's own default: the run is sent no `want` at all, so
    // how many to propose stays the server's decision unless a number is
    // explicitly picked.
    const [want, setWant] = useState("");
    const [parentId, setParentId] = useState(parent ? String(parent.id) : "");
    const [phase, setPhase] = useState("pick");     // pick | running | review | writing | done
    const [run, setRun] = useState(null);
    const [res, setRes] = useState(null);
    const [sel, setSel] = useState([]);
    const [wrote, setWrote] = useState(null);
    const [err, setErr] = useState(null);
    const stop = useRef(null);
    useEffect(() => () => { if (typeof stop.current === "function") stop.current(); }, []);

    useEffect(() => {
      let cancel = false;
      if (siteId == null) { setOpts({ loading: false, error: "No active site.", data: null }); return; }
      (async () => {
        try {
          const d = await api(`/research/topics/grow/options?site_id=${siteId}`);
          if (!cancel) {
            setOpts({ loading: false, error: null, data: d });
            // The depth rule is the server's (topic-tree.js MAX_DEPTH), and
            // this reply is where the client learns it, so it is handed up for
            // every tooltip on the screen to prefer over the local fallback.
            const md = Number(d && d.max_depth);
            if (onDepth && Number.isFinite(md) && md > 0) onDepth(md);
          }
        } catch (e) {
          // A failed read is not a site with nothing to grow from: the seed can
          // still be typed, and the reason is shown rather than swallowed.
          if (!cancel) setOpts({ loading: false, error: e.message || "Could not read what this site can grow from.", data: null });
        }
      })();
      return () => { cancel = true; };
    }, [siteId]);

    const data    = opts.data || {};
    const seedTypes = seedTypesOf(data);
    // The server may not accept the type this panel opened on, and a picker
    // sitting on a choice the run would refuse is a control that lies, so the
    // selection moves to the first type the server does accept.
    useEffect(() => {
      if (opts.data && !seedTypes.some(t => t[0] === seedType)) pickType(seedTypes[0][0]);
    }, [opts.data]);   // eslint-disable-line react-hooks/exhaustive-deps
    // How many one run may propose, and the run's own default when the reply
    // names one. Absent either, the selector is not rendered at all: offering
    // numbers this screen cannot promise the server honors would be the kind
    // of control this file exists to avoid.
    const maxProps = numOr(data.max_proposals);
    const defProps = numOr(data.default_proposals, data.default_want, data.want_default);
    const wantOpts = maxProps == null ? [] : [...new Set(WANT_LADDER.filter(n => n <= maxProps).concat(maxProps))].sort((a, b) => a - b);
    // The hand-picked seed draws on the same rows the keyword seed lists: the
    // server sends that list once (its seeds payload has no `keywords` entry on
    // purpose), so this maps the type onto the source it picks from.
    const srcKey  = seedType === "keywords" ? "keyword" : seedType;
    const src     = (data.seeds || {})[srcKey] || null;
    const names   = (src && Array.isArray(src.names)) ? src.names : [];
    // The rows with structure where the server sends them: {id,name} for
    // keywords (ids are what seed_ids carries), {name,count} for clusters.
    const srcRows = (src && Array.isArray(src.rows)) ? src.rows : [];
    const isGroupType = !!GROUP_SEED[seedType];
    // The same four answers the vocabulary blocks give elsewhere on this
    // screen: a source nobody reported, one whose read failed, a site that
    // genuinely has none of that thing, and a real list.
    const srcState = !src ? "unreported" : (src.ok === false ? "failed" : (names.length ? "some" : "none"));
    // What the seed is CALLED, per type. A keyword lives in this app's own
    // register and a category or a tag lives in WordPress, so the same sentence
    // about a failed read means two different things and has to say which. A
    // type the server added that this map does not know is called by its id
    // rather than by nothing.
    const seedNoun = { keyword: "keyword", category: "WordPress category", tag: "WordPress tag", cluster: "keyword cluster", keywords: "keyword" }[seedType] || seedType;
    const parents = Array.isArray(data.parents) ? data.parents : [];
    const parentTitle = parent ? parent.title
      : (parents.find(p => String(p.id) === parentId) || {}).title || null;
    const pickType = (t) => { setSeedType(t); setSeed(""); setTyped(false); setSeedIds([]); setSeedQuery(""); setErr(null); };
    const toggleSeedId = (id) => setSeedIds(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
    // What "a seed is chosen" means per shape: at least one ticked row for the
    // hand-picked set, a non-empty string for everything else.
    const ready = seedType === "keywords" ? seedIds.length > 0 : !!seed.trim();

    const start = async () => {
      if (siteId == null || !ready) return;
      setPhase("running"); setRun(null); setErr(null); setRes(null); setWrote(null);
      const runId = newRunId();
      stop.current = pollRun(runId, setRun);
      try {
        const r = await api(`/research/topics/grow`, { method: "POST", body: JSON.stringify({
          run_id: runId, site_id: siteId, seed_type: seedType,
          // One seed shape per type: the hand-picked set travels as ids (the
          // server refuses an off-fit one BY NAME rather than dropping it),
          // everything else as the one string, a cluster's being its name.
          ...(seedType === "keywords" ? { seed_ids: seedIds } : { seed: seed.trim() }),
          parent_id: parentId === "" ? null : Number(parentId),
          // undefined is dropped by JSON.stringify, so leaving the selector on
          // its default really does leave the count to the server.
          want: want === "" ? undefined : Number(want),
        }) });
        stop.current();
        setRes(r);
        // Everything is ticked EXCEPT what the site can be shown to publish
        // already. That is a default, not a decision: the row is still there
        // with its covering pages named, and ticking it is one press.
        setSel(((r && r.proposals) || []).map((p, i) => i)
          .filter(i => ((r.proposals[i].coverage || {}).status) !== "covered"));
        setPhase("review");
      } catch (e) {
        // The route writes its refusals for the user (a seed judged off-market,
        // a parent that is already a child, no API key), so they are shown as
        // they came and the picker stays where it was.
        stop.current();
        setErr(e.message || "The proposals could not be read.");
        setPhase("pick");
      }
    };

    const keep = async () => {
      const chosen = ((res && res.proposals) || []).filter((p, i) => sel.includes(i));
      if (!chosen.length || siteId == null) return;
      setPhase("writing"); setErr(null);
      let added = 0;
      const failed = [];
      // One row at a time, and a refusal does NOT stop the rest: these are
      // independent writes, the server owns the parent rules, and losing eight
      // approved topics because the ninth was refused would be the wrong trade.
      for (const p of chosen) {
        try {
          await api(`/research/topics`, { method: "POST", body: JSON.stringify({
            site_id: siteId, title: p.title,
            detail: p.detail || undefined, intent: p.intent || undefined,
            parent_id: parentId === "" ? null : Number(parentId),
          }) });
          added++;
        } catch (e) { failed.push({ title: p.title, why: e.message || "the write was refused" }); }
      }
      setWrote({ added, failed, asked: chosen.length });
      setPhase("done");
      onAdded && onAdded(added);
    };

    const toggle = (i) => setSel(s => (s.includes(i) ? s.filter(x => x !== i) : [...s, i]));
    const proposals = (res && res.proposals) || [];
    const cov = (res && res.coverage) || null;

    const foot = phase === "running" || phase === "writing" ? null
      : phase === "done" ? <button className="bi-btn bi-btn--primary" onClick={onClose}>Close</button>
      : phase === "review" ? <>
          <button className="bi-btn" onClick={onClose}>Cancel</button>
          <button className="bi-btn bi-btn--primary" disabled={!sel.length} onClick={keep}>
            <I name="plus" size={16} />Keep {sel.length} of {proposals.length}
          </button>
        </>
      : <>
          <button className="bi-btn" onClick={onClose}>Cancel</button>
          <button className="bi-btn bi-btn--ai" disabled={!ready || siteId == null} onClick={start}>
            <I name="sparkles" size={16} />Propose topics
          </button>
        </>;

    return React.createElement(window.MlBiModals._Modal, {
      title: "Grow topics",
      sub  : "From something this site already owns: a keyword, a cluster or hand-picked set of keywords, a category or a tag. Nothing is added until you keep it.",
      onClose: (phase === "running" || phase === "writing") ? () => {} : onClose,
      foot,
    }, (
      <div className="bi-modal__body">
        {err && <div className="bi-hint" style={{ color: "var(--danger)" }}>{err}</div>}

        {phase === "pick" && (
          <div style={{ paddingTop: 4 }}>
            <label className="bi-field__label">Grow from</label>
            <div className="bi-cf-sources">
              {seedTypes.map(([id, label, icon, hint]) => (
                <button key={id} className={"bi-cf-source" + (seedType === id ? " bi-cf-source--on" : "")} title={hint} onClick={() => pickType(id)}>
                  <span className="bi-cf-source__icon"><I name={icon} size={16} /></span>
                  <span><div className="bi-cf-source__name">{label}</div></span>
                </button>
              ))}
            </div>

            <label className="bi-field__label" style={{ marginTop: 12 }}>
              {(seedTypes.find(t => t[0] === seedType) || [seedType, capOf(seedType)])[1]} <span className="bi-field__req">*</span>
            </label>
            {opts.loading
              ? <div className="bi-subempty">Reading what this site can grow from…</div>
              : <>
                  {/* A read that failed is never rendered as a site with none of
                      that thing: the reason is named and a seed can still be
                      typed, because the run does not depend on this list. */}
                  {opts.error && <div className="bi-hint" style={{ color: "var(--danger)" }}>{opts.error}</div>}
                  {/* The group types never offer "type one instead": a cluster
                      the register does not hold is refused by the server (the
                      rows behind the name ARE the seed), and a hand-picked set
                      travels as ids, which cannot be typed. So their empty and
                      failed states end in a hint, not an input. */}
                  {srcState === "failed" && !isGroupType && <div className="bi-hint">This site's {seedNoun} list could not be read ({src.error}), so it cannot be offered here. Typing one still works, and the run says afterwards whether the site turned out to hold it.</div>}
                  {srcState === "failed" && isGroupType && <div className="bi-hint">This site's {seedNoun} list could not be read ({src.error}), so there is nothing to pick from. A {seedType === "cluster" ? "cluster" : "hand-picked set"} has to come from the register itself, so typing one is not offered.</div>}
                  {srcState === "none" && !isGroupType && <div className="bi-hint">{seedType === "keyword" ? "This site's keyword register is empty." : `This site's WordPress carries no ${seedNoun}.`} You can still type one, and the run will say plainly that the site does not hold it.</div>}
                  {srcState === "none" && seedType === "cluster" && <div className="bi-hint">{src.reason || "Clusters are written by the Classify run in the Keywords Planner, and this register has none yet."}</div>}
                  {srcState === "none" && seedType === "keywords" && <div className="bi-hint">This site's keyword register is empty, so there is nothing to pick. Add or import keywords in the Keywords Planner first.</div>}
                  {srcState === "unreported" && !isGroupType && <div className="bi-hint">The server did not report this site's {seedNoun} list, so it cannot be offered here. Typing one still works.</div>}
                  {srcState === "unreported" && isGroupType && <div className="bi-hint">The server did not report this site's {seedNoun} list, so there is nothing to pick from here.</div>}
                  {!isGroupType && (srcState === "some" && !typed
                    ? <select className="hds-select" value={seed} onChange={e => { if (e.target.value === "__type__") { setTyped(true); setSeed(""); } else setSeed(e.target.value); }}>
                        <option value="">Pick one of the {names.length}…</option>
                        {names.map(n => <option key={n} value={n}>{n}</option>)}
                        <option value="__type__">Type one instead…</option>
                      </select>
                    : <input className="hds-input" value={seed} autoFocus onChange={e => setSeed(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && seed.trim()) start(); }} placeholder={`The ${seedType} to grow around`} />)}
                  {!isGroupType && srcState === "some" && typed && <button className="bi-btn bi-btn--sm" style={{ marginTop: 8 }} onClick={() => { setTyped(false); setSeed(""); }}>Pick from the {names.length} this site has instead</button>}
                  {/* A cluster is picked with its size in view: "grow from
                      this" means something different at 3 keywords than at
                      40, and the count is what says which ask this is. */}
                  {seedType === "cluster" && srcState === "some" && (
                    <select className="hds-select" value={seed} onChange={e => setSeed(e.target.value)}>
                      <option value="">Pick one of the {srcRows.length}…</option>
                      {srcRows.map(r => <option key={r.name} value={r.name}>{r.name}, {plural(r.count || 0, "keyword", "keywords")}</option>)}
                    </select>
                  )}
                  {seedType === "keywords" && srcState === "some" && (srcRows.length
                    ? <KeywordMultiPick rows={srcRows} picked={seedIds} onToggle={toggleSeedId} query={seedQuery} onQuery={setSeedQuery} />
                    : <div className="bi-hint">The server's keyword list carries no ids, so a hand-picked set cannot be sent. Grow from a single keyword instead.</div>)}
                  {/* Said rather than left to the short list: seeds this
                      business has disowned are kept out of the picker, and a
                      list that is shorter than the register needs a reason. */}
                  {(seedType === "keyword" || seedType === "keywords") && src && src.off_fit > 0 && (
                    <div className="ds-card__note" style={{ marginTop: 6 }}>
                      {src.off_fit} further {src.off_fit === 1 ? "keyword is" : "keywords are"} in the register and judged off market or off topic, so they are not offered as a seed. Growing from one would spread a verdict this business has already reached.
                    </div>
                  )}
                </>}

            <label className="bi-field__label" style={{ marginTop: 12 }}>File the new topics under</label>
            {parent
              ? <div className="bi-hint" style={{ marginTop: 0 }}>The theme <b>{parent.title}</b>, because that is the row this was opened from. What comes back will be the articles that make it up.</div>
              : <select className="hds-select" value={parentId} onChange={e => setParentId(e.target.value)}>
                  <option value="">Top level, as new themes</option>
                  {parents.map(p => <option key={p.id} value={String(p.id)}>{p.title}{p.child_count ? ` (${p.child_count} under it)` : ""}</option>)}
                </select>}
            {!parent && opts.data && !opts.data.parents_ok && (
              <div className="bi-hint">The themes this could file under could not be read ({opts.data.parents_error}), so only the top level is offered.</div>
            )}

            {/* How many to ask for, offered only when the server has said its
                ceiling: a number the run would silently cut down to the cap is
                a promise this screen cannot keep. The default choice sends
                nothing and leaves the count to the server. */}
            {maxProps != null && (
              <>
                <label className="bi-field__label" style={{ marginTop: 12 }}>How many ideas</label>
                <select className="hds-select" value={want} onChange={e => setWant(e.target.value)}>
                  <option value="">{defProps != null ? `The server's default (${defProps})` : "The server's default"}</option>
                  {wantOpts.map(n => <option key={n} value={String(n)}>{n}</option>)}
                </select>
              </>
            )}
            <div className="bi-hint" style={{ marginTop: 12 }}>
              {parentTitle
                ? <>Topics go {(data.max_depth || TREE_DEPTH)} levels deep, a theme and the articles under it, so these land as articles under <b>{parentTitle}</b> and cannot themselves hold more.</>
                : <>These land as new themes at the top level, each one able to hold articles of its own. Only a theme that is already there can be picked above: a topic filed under one cannot take children, and the server refuses that by name rather than quietly flattening it.</>}
              {" Rows judged off market or off topic are left out of what the AI is shown, and are never proposed."}
            </div>
          </div>
        )}

        {phase === "running" && (
          /* The server's own milestones (GET /api/runs/:id), so a stalled run
             stops moving instead of looking healthy. */
          <div className="bi-fetch-progress">
            <div className="ml-progress-row" style={{ marginBottom: 12 }}>
              <div className="ml-progress"><div className="ml-progress__bar ml-progress__bar--ai" style={{ width: (run ? run.pct : 0) + "%" }} /></div>
              <span className="ml-progress-row__pct">{run ? run.pct : 0}%</span>
            </div>
            <div className="bi-fetch-step bi-fetch-step--active">
              <span className="bi-fetch-step__dot"><span className="ml-spinner ml-spinner--sm ml-spinner--ai" style={{ width: 14, height: 14 }} /></span>
              <span className="bi-fetch-step__label">
                {run ? `Step ${Math.max(1, run.step)} of ${run.steps} · ${run.label}` : "Waiting for the server to start"}
                {run && run.note ? <span style={{ color: "var(--text-muted)" }}> · {run.note}</span> : null}
              </span>
            </div>
          </div>
        )}

        {(phase === "review" || phase === "writing") && res && (
          <div style={{ paddingTop: 4 }}>
            {/* What the run was given and what it threw away, so a short list is
                explained rather than looking like a thin answer. `material` is
                read defensively because a throw here would take the whole panel
                down, taking the proposals with it. */}
            <p className="research-cost-note" style={{ marginTop: 0 }}>
              Grown from the {res.seed.type} <b>{res.seed.value}</b>
              {res.parent ? <>, under <b>{res.parent.title}</b></> : ", at the top level"}.
              {/* Whether the site really holds that seed is the server's answer,
                  in its words: it knows the difference between a term the site
                  does not use and a list nobody could read. */}
              {res.seed.note ? " " + res.seed.note : ""}
              {" "}It was shown the {plural((res.material || {}).shown || 0, "topic", "topics")} already filed there
              {(res.material || {}).off_fit ? `, and ${res.material.off_fit} more were held back as off market or off topic` : ""}.
              {(res.dropped_summary || []).length
                ? " " + res.dropped_summary.map(d => `${d.n} ${d.n === 1 ? "proposal" : "proposals"} ${d.says}`).join(", ") + "."
                : ""}
              {res.bank && res.bank.capped ? ` The duplicate check read ${res.bank.titles_read} of the ${res.bank.total} titles in this bank, so a repeat past that could not be ruled out.` : ""}
              {res.truncated ? " The reply was cut short, so there may be fewer here than asked for." : ""}
            </p>
            {/* Coverage in the server's own words: it says which of the three
                answers it could give, and why. */}
            {cov && cov.note && <p className="research-cost-note">{cov.note}</p>}

            {proposals.length === 0
              ? <div className="bi-hint">Nothing came back that is not already in the bank. Try another seed, or a different theme to file under.</div>
              : <div className="bi-checklist">
                  {proposals.map((p, i) => {
                    const c = p.coverage || {};
                    // Never the "duplicate" badge: it counts the pages it is
                    // handed, and the server sends at most three of them. The
                    // server's own sentence says how many matched, and the
                    // pages it did send are named below.
                    const state = PROP_COV[c.status] || "unknown";
                    return (
                      <label className="bi-checkrow" key={p.title + i} style={{ alignItems: "flex-start" }} onClick={() => phase === "review" && toggle(i)}>
                        <span className="hds-check"><input type="checkbox" readOnly checked={sel.includes(i)} /><span className="hds-check__box"><I name="check" size={12} /></span></span>
                        <span style={{ flex: 1 }}>
                          <span className="bi-checkrow__name">{p.title}</span>
                          <span style={{ display: "block", marginTop: 4 }}>{covBadge(state, { posts: c.posts || [] }, c.why || null)}</span>
                          {p.detail && <span className="bi-checkrow__sub" style={{ display: "block" }}>{p.detail}</span>}
                          {p.why && <span className="bi-checkrow__sub" style={{ display: "block", fontStyle: "italic" }}>{p.why}</span>}
                          {/* Named, not counted: a proposal the site may
                              already publish is a decision the user makes
                              against the actual pages. */}
                          {(c.posts || []).length > 0 && (
                            <span style={{ display: "block", marginTop: 6 }}>
                              {c.posts.map(pg => <CoveringPost key={pg.post_id} p={pg} />)}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>}
            {phase === "writing" && <div className="bi-hint" style={{ marginTop: 12 }}><span className="ml-spinner ml-spinner--sm" style={{ width: 14, height: 14, marginRight: 8, verticalAlign: "-2px" }} />Adding {plural(sel.length, "topic", "topics")} to the bank…</div>}
          </div>
        )}

        {phase === "done" && wrote && (
          <div style={{ paddingTop: 4 }}>
            <div className="bi-hint">
              {wrote.added
                ? <>Added {plural(wrote.added, "topic", "topics")} to the bank{parentTitle ? <> under <b>{parentTitle}</b></> : " at the top level"}, as new.</>
                : "Nothing was added."}
            </div>
            {/* Every refusal named. A count alone would leave the user unable to
                tell a rejected parent from a duplicate title. */}
            {wrote.failed.length > 0 && (
              <div className="bi-hint" style={{ color: "var(--danger)" }}>
                {plural(wrote.failed.length, "topic", "topics")} of the {wrote.asked} could not be added:
                <ul style={{ margin: "6px 0 0 18px" }}>
                  {wrote.failed.map(f => <li key={f.title}>{f.title}: {f.why}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    ));
  }

  // ── Promotion: the facets do the assembling ───────────────
  // "Plan" used to create a plan holding the one row it was pressed on, so a
  // plan promoted from a topic had no keyword at all. "Generate draft" runs per
  // PIECE and briefs on what that piece claims, so it could not run against one:
  // the user had to open the Keywords Planner and attach a keyword by hand. On
  // production 16 of 22 pieces were blocked on exactly that.
  //
  // GET /content-plans/assemble-preview answers what a promotion would pull in:
  // the FREE bank items sharing the seed's cluster, category or product
  // (backend/lib/assemble.js). It writes nothing and spends nothing, which is
  // why it can run on every press. It is SHOWN rather than applied quietly,
  // because a plan that mysteriously acquired six ingredients is a plan the user
  // then has to audit.
  const KIND_LABEL = { keyword: "Keywords", topic: "Topics", page: "Pages", faq: "FAQs", gap: "Gaps" };
  const KIND_NOUN  = { keyword: "keyword", topic: "topic", page: "page", faq: "FAQ", gap: "gap" };
  const LABEL_COL  = { keyword: "keyword", topic: "title", page: "title", faq: "question", gap: "title" };
  const labelOf = (kind, r) => String((r && (r[LABEL_COL[kind]] || r.title)) || "").trim();
  const nOfKind = (kind, n) => n + " " + KIND_NOUN[kind] + (n === 1 ? "" : "s");
  const listOf = (xs) => (xs.length < 2 ? (xs[0] || "") : xs.slice(0, -1).join(", ") + " and " + xs[xs.length - 1]);

  // Kinds the assembler cannot be seeded from at all (assemble.js SEED_KINDS is
  // keyword, topic, faq, gap). A page sent to the preview comes back as a 400
  // written for a developer, so this screen answers for it instead. FAQs and
  // gaps DO reach the endpoint: they carry no facets either, but the server's
  // own reason for them is the accurate one and cannot drift from the rule.
  const NO_FACETS = {
    page: "The Pages bank carries no cluster, category or product, so there is nothing to match the rest of the banks on. Assemble from the keyword or topic this page is about instead.",
  };
  // Where the facets come from, per bank, so the reason points at a control that
  // exists rather than at classification in the abstract. Only these two banks
  // have a classifier at all (/api/classify writes topics and keywords, nothing
  // else), so no other kind is offered classification as the way out.
  const CLASSIFY_HINT = {
    topic  : "Classify, at the top of this screen, gives a topic its intent, format and category. With those, promoting assembles by itself.",
    keyword: "Classify, in the Keywords Planner, gives a keyword its cluster and category. With those, promoting assembles by itself.",
  };

  // One request creates the plan AND its first piece carrying the assembled
  // ingredients, so the material never lands in the plan's unassigned pool where
  // no brief would read it. Answers with the plan plus { assembled, piece }, and
  // `piece` is null when the assembly came back with nothing.
  //
  // The seed is ALSO sent as an ordinary ingredient. The preview is a separate
  // read, so the banks can move between previewing and writing; that floor is
  // what keeps the promoted row inside its own plan even when the assembly then
  // finds nothing, instead of leaving a plan holding nothing at all.
  const createAssembled = (siteId, kind, it) => api(`/content-plans`, { method: "POST", body: JSON.stringify({
    site_id: siteId, title: it.title, status: "idea", source: "research",
    source_ref: `${kind}:${it.id}`,
    primary_keyword: kind === "keyword" ? it.title : null,
    ingredients: [{ kind, ref_id: Number(it.id), ref_label: it.title }],
    assemble: { kind, ref_id: Number(it.id) },
  }) });

  // The promotion as it was, for a seed nothing can be assembled from: a plan
  // holding exactly the row promoted, sent as a precise reference rather than a
  // name so nothing has to be matched back by string afterwards.
  const createSingle = (siteId, kind, it) => api(`/content-plans`, { method: "POST", body: JSON.stringify({
    site_id: siteId, title: it.title, status: "idea", source: "research",
    source_ref: `${kind}:${it.id}`,
    primary_keyword: kind === "keyword" ? it.title : null,
    ingredients: [{ kind, ref_id: Number(it.id), ref_label: it.title }],
  }) });

  // Read from the WRITE's own answer rather than from the preview: the preview
  // is what we expected, the piece is what exists. Its claimed keywords are the
  // test, not primary_keyword, which the server deliberately leaves null so the
  // brief can pick the strongest one the piece actually consumes. An answer this
  // cannot read counts as false, so it may under-report but can never claim a
  // draft is possible when it is not.
  const pieceCanGenerate = (piece) => {
    const kws = piece && piece.ingredients && piece.ingredients.keyword;
    if (Array.isArray(kws)) return kws.length > 0;
    return !!(piece && piece.primary_keyword);
  };
  const pieceTakes = (piece) => Object.entries((piece && piece.ingredients) || {})
    .filter(([, v]) => Array.isArray(v) && v.length).map(([k, v]) => nOfKind(k, v.length));
  // Why the server assembled nothing, in its words. It knows the rule; this
  // screen only knows what it asked for.
  const noneReason = (res) => ((res && res.assembled && res.assembled.reason) || "the banks answered with nothing to assemble");

  // A plan holding one row is not a failure, it is a plan that cannot generate
  // yet, and it has to say so or it looks identical to one that can. Written
  // once so the modal's warning and the toast afterwards cannot drift apart.
  const soloTail = (kind, noun) => (kind === "keyword"
    ? "just this keyword. It has no piece yet, so nothing can generate a draft from it until you add one in the Content Planner."
    : `just this ${noun}. It has no keyword and no piece, so nothing can generate a draft from it yet.`);
  const soloNote = (kind, noun, title) => `Plan created: "${title}", holding ${soloTail(kind, noun)}`;

  /**
   * What promoting this row would pull in, before it pulls it in.
   *
   * Two outcomes, and neither of them blocks the user. Assembled: the material
   * is listed with what it matched on, and the modal says plainly whether the
   * resulting piece can generate a draft. Not assembled: the reason is shown
   * (the seed has no facets, so there is nothing to match on) and the old
   * single-item promotion is still offered, because refusing to promote would
   * take away something that worked before.
   */
  function PlanPreview({ siteId, kind, item, noun, onClose, onDone }) {
    const [phase, setPhase] = useState("loading");   // loading | ready | blocked | error
    const [data, setData] = useState(null);
    const [reason, setReason] = useState(null);
    const [err, setErr] = useState(null);
    const [busy, setBusy] = useState(false);
    const [tick, setTick] = useState(0);

    useEffect(() => {
      let cancel = false;
      if (NO_FACETS[kind]) { setReason(NO_FACETS[kind]); setPhase("blocked"); return; }
      setPhase("loading"); setErr(null); setReason(null);
      (async () => {
        try {
          const r = await api(`/content-plans/assemble-preview?site_id=${siteId}&kind=${kind}&ref_id=${item.id}`);
          if (cancel) return;
          // ok:false is an answer, not a failure: classification has not run, so
          // there is nothing to match on, and the reason names what fixes that.
          if (r && r.ok) { setData(r); setPhase("ready"); }
          else { setReason((r && r.reason) || "This one cannot be assembled, and the server did not say why."); setPhase("blocked"); }
        } catch (e) {
          if (cancel) return;
          // A preview that could not be read is not a reason to refuse the
          // promotion. It only means this screen cannot say what it would pull.
          setErr(e.message || "Could not read the preview."); setPhase("error");
        }
      })();
      return () => { cancel = true; };
    }, [siteId, kind, item.id, tick]);

    const run = async (assembled) => {
      setBusy(true); setErr(null);
      try {
        if (assembled) {
          const res = await createAssembled(siteId, kind, item);
          const piece = res && res.piece;
          // The preview said one thing and the write is allowed to say another:
          // the banks can move between the two reads. What gets reported is the
          // write's answer, never the preview's expectation.
          if (!piece) {
            onDone(`Plan created: "${item.title}", but nothing was assembled into it after all: ${noneReason(res)} It holds ${soloTail(kind, noun)}`, false);
            return;
          }
          const can = pieceCanGenerate(piece);
          onDone(`Plan created: "${item.title}". Its piece takes ${listOf(pieceTakes(piece)) || "the seed on its own"}`
            + (can ? ", and it can generate a draft." : ", but no keyword, so it cannot generate a draft yet."), can);
        } else {
          await createSingle(siteId, kind, item);
          onDone(soloNote(kind, noun, item.title), false);
        }
      } catch (e) { setBusy(false); setErr(e.message || "Could not create the plan."); }
    };

    const seed = data && data.seed;
    const groups = data ? Object.entries(data.ingredients || {}).filter(([, v]) => Array.isArray(v) && v.length) : [];
    const matched = (data && data.matched_on) || [];
    const counted = data ? Object.entries(data.counts || {}).filter(([, n]) => n > 0).map(([k, n]) => nOfKind(k, n)) : [];

    const foot = phase === "loading"
      ? <button className="bi-btn" onClick={onClose}>Cancel</button>
      : phase === "ready"
        ? <>
            <button className="bi-btn" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="bi-btn bi-btn--primary" onClick={() => run(true)} disabled={busy}><I name="arrow-right" size={16} />{busy ? "Creating…" : "Create the plan"}</button>
          </>
        : <>
            <button className="bi-btn" onClick={onClose} disabled={busy}>Cancel</button>
            {phase === "error" && <button className="bi-btn" onClick={() => setTick(t => t + 1)} disabled={busy}><I name="refresh" size={16} />Try again</button>}
            <button className="bi-btn bi-btn--primary" onClick={() => run(false)} disabled={busy}>{busy ? "Creating…" : `Promote just this ${noun}`}</button>
          </>;

    return React.createElement(window.MlBiModals._Modal, {
      title: "Plan this " + noun,
      sub: "What the banks already hold about it, pulled in before the plan exists.",
      onClose: busy ? () => {} : onClose,
      foot,
    }, (
      <div className="bi-modal__body">
        {phase === "loading" && <div className="bi-subempty">Reading what this would pull in…</div>}

        {phase === "ready" && data && (
          <>
            <div className="bi-hint">
              Every item below shares this {noun}'s {listOf(matched) || "facets"} and no live plan is holding it.
              Nothing has been written yet, and none of this costs tokens: the match is the site's own vocabulary, not a model's opinion.
            </div>
            <div className="bi-card__meta" style={{ marginTop: 12 }}>
              <div className="bi-meta-row"><span className="bi-meta-row__l">Seed</span><span className="bi-meta-row__v">{(seed && seed.label) || item.title}</span></div>
              <div className="bi-meta-row"><span className="bi-meta-row__l">Matched on</span><span className="bi-meta-row__v">{listOf(matched) || "nothing further"}</span></div>
              <div className="bi-meta-row"><span className="bi-meta-row__l">The piece takes</span><span className="bi-meta-row__v">{listOf(counted) || "the seed on its own"}{data.total ? ` (${data.total} in all)` : ""}</span></div>
            </div>
            {groups.map(([k, list]) => (
              <div key={k} style={{ marginTop: 12 }}>
                <div className="bi-field__label">{KIND_LABEL[k] || k} ({list.length})</div>
                <div className="ds-card__sample">
                  {list.map(g => {
                    const isSeed = seed && k === seed.kind && Number(g.ref_id) === Number(seed.ref_id);
                    return <span key={k + ":" + g.ref_id} className={"ds-chip" + (isSeed ? " ds-chip--subject" : "")} title={isSeed ? "The row you pressed Plan on" : g.ref_label}>{g.ref_label}</span>;
                  })}
                </div>
              </div>
            ))}
            {/* The one number that decides whether this was worth doing. A piece
                with no keyword has nothing for the generator to target, and that
                is the difference between a plan that works and one to repair. */}
            <div className="bi-hint" style={{ marginTop: 12 }}>
              {data.has_keyword
                ? <><I name="check-circle" size={14} style={{ color: "var(--success)", verticalAlign: "-2px", marginRight: 6 }} />
                    <b>This piece can generate a draft.</b> It carries {nOfKind("keyword", (data.counts && data.counts.keyword) || 0)}, and the strongest is what its brief targets.</>
                : <><I name="alert-triangle" size={14} style={{ color: "var(--warning)", verticalAlign: "-2px", marginRight: 6 }} />
                    <b>This piece cannot generate a draft yet.</b> No free keyword shares these facets, so it would be created with nothing to target. Classify more keywords, or attach one to the piece in the Content Planner afterwards.</>}
            </div>
          </>
        )}

        {(phase === "blocked" || phase === "error") && (
          <>
            {phase === "error"
              ? <div className="bi-hint" style={{ color: "var(--danger)" }}>Could not read what this would pull in: {err}</div>
              : <div className="bi-hint">{reason}</div>}
            {phase === "blocked" && CLASSIFY_HINT[kind] && (
              <div className="bi-hint" style={{ marginTop: 12 }}>
                <I name="sparkles" size={14} style={{ color: "var(--indigo-500)", verticalAlign: "-2px", marginRight: 6 }} />
                {CLASSIFY_HINT[kind]}
              </div>
            )}
            <div className="bi-hint" style={{ marginTop: 12 }}>
              Promoting still works and does what it did before: a plan holding {soloTail(kind, noun)}
            </div>
          </>
        )}

        {err && phase !== "error" && <div className="bi-hint" style={{ marginTop: 12, color: "var(--danger)" }}>{err}</div>}
      </div>
    ));
  }

  // ── The vocabulary a classification run uses ───────────────
  //
  // WHY IT IS ON SCREEN BEFORE THE RUN. Facet assembly groups by SHARED
  // category (backend/lib/assemble.js), so a run with nothing to classify into
  // is worse than no run: the model invents a bucket per row, and on the live
  // site that produced 34 categories over 38 items, 30 of them holding one
  // thing. Buckets nothing else shares connect nothing, and the tokens are
  // spent either way, so what a run classifies INTO has to be visible first.
  //
  // TWO FACETS HERE, NOT THREE. The category leads, because it is what decides
  // whether a promoted topic can assemble at all. Tags follow as a note: they
  // are SUPPOSED to be granular, so a bank with many of them is not the fault
  // and the rule is only reuse before inventing, which is a weaker constraint
  // than the one on categories and reads like one.
  //
  // There is deliberately NO cluster here. This bank has no cluster column and
  // POST /api/classify/topics does not ask for one, so naming the concept on
  // this screen would offer a facet that cannot exist. The Keywords Planner is
  // where clusters are read and written.
  //
  // GET /classify/vocabulary answers it, and the classify POST reports what it
  // applied for each facet. Both are read through readVocab, tolerantly: the
  // names and the count are what this screen says, and which key they arrive
  // under is the backend's business. A facet the payload never mentions reads
  // as null, meaning "not reported", which is never rendered as "none": one is
  // a server that does not say, the other is a site that genuinely has none of
  // that facet, and a site can easily have categories and no tags.
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const SOURCE_SAYS = {
    wordpress: "from WordPress", wp: "from WordPress", wp_category: "from WordPress",
    category: "from WordPress", categories: "from WordPress",
    suggestion: "an approved suggestion", suggestions: "an approved suggestion",
    taxonomy_suggestion: "an approved suggestion", taxonomy_suggestions: "an approved suggestion",
    approved: "an approved suggestion", approved_suggestions: "from approved suggestions",
  };
  const sourceSays = (k) => SOURCE_SAYS[String(k).toLowerCase()] || String(k).replace(/_/g, " ");
  // A per-source tally on the envelope ({ wordpress: 6, suggestions: 2 }), the
  // same fact as a source per name, which the endpoint may send instead. A
  // source may also arrive as its own { ok, count, error } record, and either
  // way only what it CONTRIBUTED is named: a source that failed adds nothing
  // here rather than a confident "0 from WordPress".
  function declaredSources(box) {
    const d = box && box.sources;
    if (!d || typeof d !== "object" || Array.isArray(d)) return [];
    return Object.entries(d)
      .map(([k, v]) => [k, Number(v && typeof v === "object" ? v.count : v)])
      .filter(([, n]) => Number.isFinite(n) && n > 0)
      .map(([k, n]) => `${n} ${sourceSays(k)}`);
  }

  // Where one facet's answer may arrive: a plain list beside the categories, an
  // envelope per facet carrying its own count, sources and ok/error, or a bare
  // count with no list at all. Clusters are absent by design, so no reading of
  // any payload can put one on this screen.
  const FACET_KEYS = { categories: ["categories", "category"], tags: ["tags", "tag"] };
  const LIST_KEYS = ["names", "terms", "list", "values"];
  const numOr = (...vs) => { for (const v of vs) { if (v == null || v === "") continue; const n = Number(v); if (Number.isFinite(n)) return n; } return null; };
  const arrIn = (o, keys) => { for (const k of keys) if (Array.isArray(o && o[k])) return o[k]; return null; };

  /**
   * ONE facet of the vocabulary as { names, total, sources, error }, or null
   * when the payload never mentions that facet at all.
   *
   * `error` is that facet's OWN failed read, which the endpoint reports per
   * source. It is a different answer from a total of 0 and must never be shown
   * as one: a bank with no tags and a tag list nobody could read lead to
   * opposite conclusions.
   */
  function readFacet(raw, facet) {
    if (!raw || typeof raw !== "object") return null;
    // `vocabularies` is the uniform map the endpoint restates every facet under,
    // and it is read FIRST because the classify responses put the category facet
    // alone under `vocabulary`: looking there for tags would find nothing and
    // report a facet the run really did apply as one the server never mentioned.
    const env  = Array.isArray(raw) ? raw : (raw.vocabularies || raw.vocabulary || raw.vocab || raw);
    const keys = FACET_KEYS[facet];
    const cat  = facet === "categories";
    // A bare array payload IS the category list, and says nothing about the rest.
    if (Array.isArray(env) && !cat) return null;

    let held = null;
    if (Array.isArray(env)) held = env;
    else for (const k of keys) if (env[k] != null) { held = env[k]; break; }
    const own  = held && !Array.isArray(held) && typeof held === "object" ? held : null;
    const list = Array.isArray(held) ? held : (own ? (arrIn(own, keys) || arrIn(own, LIST_KEYS)) : null);
    const flat = Array.isArray(env) ? {} : env;

    // The envelope's own total counts CATEGORIES, which are the payload's
    // subject. A tag count has to be stated for itself, or this would report
    // the category count as if it were the tags'.
    const stated = own ? numOr(own.total, own.count)
      : cat ? numOr(flat.total, flat.count)
      : numOr(...keys.map(k => flat[k + "_total"]), ...keys.map(k => flat[k + "_count"]));
    const box = own || (cat ? flat : null);
    let said = own || (cat ? flat : null);
    if (!said) for (const k of keys) if (flat[k + "_ok"] != null || flat[k + "_error"] != null) { said = { ok: flat[k + "_ok"], error: flat[k + "_error"] }; break; }
    const error = (said && (said.ok === false || said.error))
      ? (String(said.error || "").trim() || "The server did not say why.")
      : null;

    if (!list) {
      // A count with no list is still an answer, and a count of zero is the one
      // that matters most, so it is read rather than dropped as unreported. A
      // facet with no list, no count and no error was not reported at all, and
      // guessing "none" for it would be the invention this exists to prevent.
      if (stated == null) return error ? { names: [], total: 0, sources: [], error } : null;
      return { names: [], total: stated, sources: box ? declaredSources(box) : [], error };
    }
    const names = [];
    const bySource = new Map();
    for (const c of list) {
      const name = typeof c === "string" ? c.trim() : String((c && (c.name || c.term || c.label || c[keys[1]])) || "").trim();
      if (!name) continue;
      names.push(name);
      const from = (c && typeof c === "object" && (c.source || c.from || c.origin)) || null;
      if (from) { const said2 = sourceSays(from); bySource.set(said2, (bySource.get(said2) || 0) + 1); }
    }
    const counted = [...bySource.entries()].map(([l, n]) => `${n} ${l}`);
    // A stated total wins only when it exceeds what was listed: a capped list
    // must not read as the whole vocabulary, and a total below the names on
    // screen would contradict them.
    return {
      names,
      total  : stated != null && stated > names.length ? stated : names.length,
      sources: counted.length ? counted : (box ? declaredSources(box) : []),
      error,
    };
  }

  /**
   * The whole answer, facet by facet. Always an object: it is the FACETS inside
   * that are null when the payload does not report them, so nothing asking
   * about tags can ever be handed the categories instead.
   */
  function readVocab(raw) {
    return { categories: readFacet(raw, "categories"), tags: readFacet(raw, "tags") };
  }

  // The four answers ONE facet can give, decided in one place so nothing can
  // render a failed read as an empty one:
  //   unreported - the payload never mentioned this facet
  //   failed     - its read failed, so what the site has is not known
  //   none       - the site genuinely has none of it
  //   some       - a real vocabulary
  function facetState(f) {
    if (!f) return "unreported";
    if (f.error && !f.total) return "failed";
    return f.total ? "some" : "none";
  }
  // A vocabulary that IS there but whose read half failed is short by an unknown
  // amount, and saying so is the difference between a list and a complete one.
  const shortBy = (f) => (f && f.total && f.error ? ` One source did not answer, so this list may be short: ${f.error}` : "");

  /** Names on screen before the rest is counted rather than listed. */
  const VOCAB_SHOWN = 8;
  function VocabNames({ v }) {
    const shown = v.names.slice(0, VOCAB_SHOWN);
    if (!shown.length) return null;
    return (
      <div className="ds-card__sample">
        {shown.map((n, i) => <span className="ds-chip" key={n + ":" + i}>{n}</span>)}
        {v.total > shown.length && <span className="ds-card__note">and {v.total - shown.length} more</span>}
      </div>
    );
  }

  // Said once, so the warning before a run and the finding after it cannot
  // drift into two explanations of the same thing.
  const NO_VOCAB_WHY = "There is nothing to classify into, so the classifier invents a bucket per topic. A bucket only one topic holds groups nothing: facet assembly pulls material into a piece by SHARED category, which is what leaves a promoted piece unable to reach a keyword.";
  const NO_VOCAB_FIX = "Taxonomy Library proposes at most 8 broad categories drawn from this site's own content, and creates them in WordPress when you approve them. That is the vocabulary this would then classify into.";

  /**
   * The way to the screen that MAKES a vocabulary.
   *
   * `taxonomy-suggestions` is the id main.jsx routes on and the one data.jsx
   * declares, and the label and its nav group are read from that same nav data,
   * so a renamed item renames itself here too.
   *
   * A missing `go` is not treated as a reason to hide the way out, and not as a
   * reason to render a button either: with no navigator the destination is named
   * in words instead. A control that would do nothing when pressed is the one
   * thing this must never be.
   */
  const TAXONOMY_SCREEN = "taxonomy-suggestions";
  function taxonomyLabel() {
    for (const g of ((window.MlData && window.MlData.NAV_GROUPS) || [])) {
      for (const it of (g.items || [])) if (it.id === TAXONOMY_SCREEN) return it.label;
    }
    return "Taxonomy Library";
  }
  function taxonomyPath() {
    for (const g of ((window.MlData && window.MlData.NAV_GROUPS) || [])) {
      for (const it of (g.items || [])) if (it.id === TAXONOMY_SCREEN) return `${g.label ? g.label + " · " : ""}${it.label}`;
    }
    return "Content · Taxonomy Library";
  }
  function TaxonomyLink({ onGo }) {
    if (!onGo) return <span className="ds-card__note">Open {taxonomyPath()} from the sidebar to build one</span>;
    return <button className="bi-btn bi-btn--sm" onClick={onGo}><I name="sparkles" size={14} />Open {taxonomyLabel()}</button>;
  }

  /**
   * What this run would classify into, in the confirm dialog, before the tokens.
   *
   * ONE brief covering both facets that exist here, ranked, rather than a second
   * brief bolted beside the first. Two of the four answers are screen-wide
   * (still reading, and a read that failed outright) and the other two are per
   * facet (a facet the server did not report, and a site that genuinely has none
   * of it), because a site can easily have categories and no tags.
   *
   * Only an empty CATEGORY vocabulary is a reason not to run, and only it is
   * offered the way out: an empty tag list costs a run nothing.
   *
   * The caller wraps this in .ds-brief, which spaces the blocks, so each one is
   * a plain sibling and carries no margin of its own.
   */
  function VocabBrief({ vocab, onTaxonomy }) {
    if (vocab.loading) return <div>Still reading which categories and tags this site classifies into.</div>;
    if (vocab.error) {
      return (
        <div>
          Could not read which categories and tags this site classifies into: {vocab.error} The run would still use
          whatever the server holds; this screen cannot say what that is.
        </div>
      );
    }
    const v = vocab.data || {};
    const cat = v.categories, tg = v.tags;

    const category = () => {
      switch (facetState(cat)) {
        case "some": return (
          <div>
            It classifies into the {plural(cat.total, "category", "categories")} this site already uses
            {cat.sources.length ? ` (${cat.sources.join(", ")})` : ""}. Sharing one of them is how facet assembly pulls
            a topic into a piece, so topics landing in these can be assembled with the keywords that carry them.
            {shortBy(cat)}
            <VocabNames v={cat} />
          </div>
        );
        case "none": return (
          <>
            <div><b>This site has no categories to classify into.</b> {NO_VOCAB_WHY} {NO_VOCAB_FIX}</div>
            <div><TaxonomyLink onGo={onTaxonomy} /></div>
          </>
        );
        case "failed": return (
          <div>
            Could not read which categories this site classifies into: {cat.error} The run would still use whatever the
            server holds; this screen cannot say what that is.
          </div>
        );
        default: return <div>The server did not say which categories this site classifies into, so this screen cannot show them before the run.</div>;
      }
    };

    // Tags trail as a note, and say a different thing from the category line on
    // purpose: many tags is not the fault, so this is a preference for reuse
    // rather than the hard constraint above it.
    const tags = () => {
      switch (facetState(tg)) {
        case "some": return <div className="ds-card__note">Tags: the run can reuse the {tg.total} this site already uses. Tags are meant to be granular, so a new one is not a fault by itself; reaching for one that already fits before inventing a near-duplicate is all this asks.</div>;
        case "none": return <div className="ds-card__note">Tags: this site has none yet, so every tag this run writes is a new one. Nothing assembles on tags, so that is housekeeping rather than a blocker.</div>;
        case "failed": return <div className="ds-card__note">Tags: could not read which ones this site already uses ({tg.error}), so this screen cannot say what the run can reuse.</div>;
        default: return <div className="ds-card__note">Tags: the server did not say which ones this site already uses.</div>;
      }
    };

    return <>{category()}{tags()}</>;
  }

  /**
   * What the run decided about BELONGING, which is a separate question from
   * what it decided about category and format, and is reported separately.
   *
   * Numbers that must not merge: what it judged, what it left alone because a
   * person had already decided, what came back without a readable verdict
   * (still unjudged, not "unclear"), and how much of the bank has still never
   * been judged. A run that judged 20 of 400 has to read as one.
   *
   * A run that judged NOTHING because the site has no business profile is not a
   * failure and not a clean sweep. It says which it was in the server's own
   * words, since the server is what knows why.
   */
  function FitReport({ fit }) {
    if (!fit) return null;
    if (!fit.asked) return <p className="ds-card__detail ds-card__note">No topic was judged for belonging. {fit.why}</p>;
    const c = fit.counts || {};
    const split = ["relevant", "unclear", "off_market", "off_topic"]
      .filter(k => c[k]).map(k => `${c[k]} ${FIT_LABEL[k].toLowerCase()}`);
    const bank = fit.bank || {};
    return (
      <>
        <p className="ds-card__detail">
          {fit.judged
            ? `Judged ${plural(fit.judged, "topic", "topics")} against this site's business profile: ${split.join(", ")}.`
            : "Nothing new was judged against this site's business profile."}
          {fit.kept_by_hand ? ` ${plural(fit.kept_by_hand, "verdict", "verdicts")} you had set by hand were left exactly as they were.` : ""}
          {fit.unreadable ? ` ${plural(fit.unreadable, "row", "rows")} came back without a verdict this could read, so they stay unjudged rather than being called unclear.` : ""}
          {" Nothing was deleted or archived: an off-market topic is hidden by the Fit filter below and is still in the bank."}
        </p>
        {fit.why && <p className="ds-card__detail ds-card__note">{fit.why}</p>}
        {bank.ok === false
          ? <p className="ds-card__detail ds-card__note">How many topics are still unjudged could not be read: {bank.error}</p>
          : bank.unjudged > 0
            ? <p className="ds-card__detail ds-card__note">{bank.unjudged} of the {bank.total} topics in this bank have still never been judged. Classify again to work through them.</p>
            : null}
      </>
    );
  }

  /**
   * What one classification run did, kept on screen until dismissed.
   *
   * This used to be a toast, and a toast cannot carry the one thing that
   * decides whether the run was worth anything: which vocabulary it applied. A
   * run into the site's own categories and a run that invented 34 buckets for 38
   * rows both said "Classified 38 topics" and then faded.
   *
   * Three numbers stay apart because they are three facts: `classified` landed
   * in the database, `skipped` were in the batch but the AI reply was cut short
   * before reaching them (so they kept the facets they had), and `remaining`
   * were past the server's cap and never sent. A request that FAILED says what
   * the server said; a press with NOTHING to classify never reached the AI and
   * is not a failure; a run that ran and classified nothing IS one, whatever the
   * response code was.
   *
   * It renders only after a press. A bank with nothing to classify says nothing
   * here.
   *
   * On screen it is ONE LINE: the outcome with its numbers, which opens a
   * drawer holding the full report (window.MlReportDrawer). The report lost
   * none of its sentences in the fold; it stopped sitting on top of the bank
   * it reports on.
   */
  function ClassifyRunReport({ run, onDismiss, onTaxonomy }) {
    const { ReportStrip } = window.MlReportDrawer;
    const vocab = run.vocab || {};
    const cat = vocab.categories, tg = vocab.tags;
    const did = run.classified > 0;
    const failed = !!run.error || (!run.empty && run.classified === 0);
    // The strip's one line carries the numbers a glance needs (what landed,
    // what the reply skipped, what is still waiting); everything else waits in
    // the drawer. Both read the same run object, so they cannot disagree.
    const base = run.error ? "Classification failed"
      : run.empty ? "Nothing to classify"
      : run.classified === 0 ? "This run classified nothing"
      : `Classified ${plural(run.classified, "topic", "topics")}`;
    const bits = [];
    if (!run.error && !run.empty) {
      if (run.skipped) bits.push(`${run.skipped} skipped`);
      if (run.remaining) bits.push(`${run.remaining} still waiting`);
    }
    const title = base + (bits.length ? `, ${bits.join(", ")}` : "");
    /* A run that classified into nothing is not a finished job, whatever its
       count says, so it never gets the settled look. */
    const tone = failed ? "problem" : (run.remaining || facetState(cat) === "none") ? "todo" : "opportunity";
    return (
      <ReportStrip title={title} tone={tone} onDismiss={onDismiss}>
        {run.error ? <p className="ds-card__detail">{run.error}</p> : run.empty ? <p className="ds-card__detail">{run.empty}</p> : (
          <p className="ds-card__detail">
            {`${run.classified} of the ${plural(run.asked, "topic", "topics")} it asked for came back classified.`}
            {run.skipped ? ` ${plural(run.skipped, "row", "rows")} in the batch kept the facets they already had: the AI reply stopped before reaching them.` : ""}
            {run.remaining ? ` ${run.remaining} were past what one run may take and were never sent, so they are still waiting.` : ""}
            {run.classified === 0 ? " Nothing in the bank changed." : ""}
          </p>
        )}
        {/* What this run applied, facet by facet. A run into the site's own
            categories and one that invented its own are the difference
            between facets that group and facets that do not, so they must
            never read alike afterwards. A facet the server did not report is
            silent here rather than reported as empty. */}
        {facetState(cat) === "some" && (
          <>
            <p className="ds-card__detail">
              {did ? "Classified into" : "The run was given"} the {plural(cat.total, "category", "categories")} this
              site already uses{cat.sources.length ? ` (${cat.sources.join(", ")})` : ""}
              {did
                ? ", so these topics share their buckets with everything else carrying them."
                : ", so whatever stopped this run, a missing vocabulary was not it."}
              {shortBy(cat)}
            </p>
            <VocabNames v={cat} />
          </>
        )}
        {facetState(cat) === "none" && (
          <p className="ds-card__detail">
            <b>No category vocabulary was applied.</b> {NO_VOCAB_WHY} {NO_VOCAB_FIX}
          </p>
        )}
        {facetState(cat) === "failed" && (
          <p className="ds-card__detail">Which categories the run classified into could not be read: {cat.error}</p>
        )}
        {facetState(tg) === "some" && (
          <p className="ds-card__detail ds-card__note">Tags: the {tg.total} this site already uses were there to be reused, and a run is free to add a granular one none of them covered.</p>
        )}
        {facetState(tg) === "none" && (
          <p className="ds-card__detail ds-card__note">Tags: this site had none to reuse, so every tag this run wrote is a new one.</p>
        )}
        {facetState(tg) === "failed" && (
          <p className="ds-card__detail ds-card__note">Tags: which ones the run could reuse could not be read ({tg.error}).</p>
        )}
        {/* Belonging, last, because it is the one finding that is not about
            how a topic is filed. It renders only when the response carried
            it: a server that said nothing about fit says nothing here. */}
        <FitReport fit={run.fit} />
        {/* Dismiss lives on the strip now, where the report is thrown away
            from; the taxonomy link stays here with the finding it answers. */}
        {facetState(cat) === "none" && (
          <div className="ds-card__foot">
            <TaxonomyLink onGo={onTaxonomy} />
          </div>
        )}
      </ReportStrip>
    );
  }

  // ── The Topics tree ───────────────────────────────────────
  //
  // A theme holds the articles that make it up, two levels deep and no more
  // (backend/lib/topic-tree.js MAX_DEPTH). The rules are the server's: this
  // screen only shapes the controls so the obvious moves are the legal ones,
  // and every refusal the server sends is shown in its own words.
  //
  // WHAT A FILTER DOES TO A CHILD. The status and coverage filters select ROWS,
  // and a child can match a filter its theme does not: "New" over a theme
  // already marked Used is exactly that. Hiding the child because its parent
  // was filtered out would silently drop a row the filter had selected, and
  // this bank is a register. So a child whose parent is not in the view is
  // shown AT THE TOP LEVEL, saying which theme it belongs to. The reverse case,
  // a theme on screen whose children the filter excluded, is stated on the
  // theme itself ("2 of 5 under this one match"), so a collapsed parent never
  // reads as a complete one.
  // The ONE client-side fallback for how deep the tree goes. The rule itself
  // is the server's (topic-tree.js MAX_DEPTH, reported as max_depth on the
  // grow options reply), and every sentence about depth prefers the reported
  // value; this constant only answers before any reply has carried one.
  const TREE_DEPTH = 2;
  function groupTree(rows, all) {
    const inView = new Set(rows.map(r => r.id));
    const byId = new Map(all.map(i => [i.id, i]));
    const kids = new Map();
    for (const r of rows) {
      const p = r.parent_id;
      if (p == null || !inView.has(p)) continue;
      if (!kids.has(p)) kids.set(p, []);
      kids.get(p).push(r);
    }
    const top = [];
    for (const r of rows) {
      const p = r.parent_id;
      if (p != null && inView.has(p)) continue;         // rendered under its theme instead
      top.push({
        item: r,
        kids: kids.get(r.id) || [],
        // The theme it belongs to but is not being shown under. null when the
        // row is genuinely at the top, and an id with no row behind it when the
        // theme is gone from the bank entirely, which is still worth saying.
        awayFrom: p == null ? null : (byId.get(p) || { id: p, title: null }),
      });
    }
    return top;
  }

  // The tab ids the shell may deep-link to, in either spelling: the tabs here
  // are singular ("topic") while the banks are spoken of in the plural
  // ("topics"), and a caller should not have to know which one this file uses.
  const TAB_ALIAS = { topics: "topic", keywords: "keyword", pages: "page", faqs: "faq" };

  /* `markets`, `marketId` and `onPickMarket` are the MARKET CONTROL, and they
   * are the shell's: a market is a fact about the WEBSITE, so the Topics Bank
   * and the Keywords Planner answering for two different markets at once would
   * be two screens disagreeing about which business is on screen
   * (client/src/main.jsx has the reasoning beside `propertyMarkets`).
   *
   * Fewer than two markets draws nothing and sends nothing, so a one market
   * website gets this screen exactly as it was, down to the request. */
  function TopicsBank({ onWrite, site, go, initialTab, markets, marketId, onPickMarket }) {
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    /* The control, resolved at RENDER time from the module that owns it, so
     * neither file depends on import order and a missing module costs the
     * control rather than the screen. Same pattern as FacetDropdown above. */
    const MarketControl = (window.MlResearchKeywords && window.MlResearchKeywords.MarketControl) || null;
    /* What the SERVER answered about the market, which is not always what was
     * asked for: a market can be asked for and refused, and the control shows
     * the answer rather than the question. Null until a read with a market in
     * it has come back. */
    const [marketAnswer, setMarketAnswer] = useState(null);
    /* One bank read, in one place, so the list and the four tab counts cannot
     * ask two different questions. `market_id` is sent only when the shell gave
     * this screen one, and the route then answers with an envelope instead of a
     * bare array: it has to carry which market answered and how many rows of
     * this website are in NO market, and an array has nowhere to put either. */
    const bankUrl = useCallback((t) => {
      const p = new URLSearchParams({ site_id: String(siteId) });
      const n = marketId == null ? NaN : Number(marketId);
      if (Number.isFinite(n) && n > 0) p.set("market_id", String(n));
      return `/${ENDPOINT[t]}?${p.toString()}`;
    }, [siteId, marketId]);
    // An array or an envelope, read the same way. The keywords tab answers with
    // an array even when a market was named, because that route's response has
    // always been one.
    const bankRows = (r) => (Array.isArray(r) ? r : (r && Array.isArray(r.items) ? r.items : []));
    // `initialTab` opens the screen on a named tab, for callers that land here
    // about one bank (the shell's badge points at topics, a page's draft link
    // points back at pages). It is the INITIAL state only, so the default
    // behavior without it is exactly what it was, and an unknown value falls
    // back to Topics rather than to a tab that does not exist.
    const wanted = TAB_ALIAS[initialTab] || initialTab;
    const [type, setType] = useState(TYPES.some(([id]) => id === wanted) ? wanted : "topic");
    const [stat, setStat] = useState("all");
    const [covFilter, setCovFilter] = useState("all");
    // Which category a topic was filed in, as a filter. "all" shows everything
    // and "__none__" is the rows no run has bucketed yet, which is the set a
    // user most often wants to see on its own.
    const [catFilter, setCatFilter] = useState("all");
    // The depth the server reported on the last grow options read, preferred
    // over the TREE_DEPTH fallback wherever depth is spoken of.
    const [treeDepth, setTreeDepth] = useState(TREE_DEPTH);
    // Off-market and off-topic rows are hidden by DEFAULT, because a bank whose
    // imported half belongs to somebody else's business is unreadable
    // otherwise. Hidden, never deleted, and the strip under the filters says
    // how many: a screen quietly withholding rows is worse than one showing
    // them.
    const [fitView, setFitView] = useState("keep");
    const [growing, setGrowing] = useState(null);   // { parent } | true
    const [cov, setCov] = useState({ loading: true, error: null, data: null });
    const [syncing, setSyncing] = useState(false);
    const [selectMode, setSelectMode] = useState(false);
    const [sel, setSel] = useState([]);
    const [bulkStat, setBulkStat] = useState("new");
    const [busy, setBusy] = useState(false);
    const [adding, setAdding] = useState(false);
    // The row whose Edit form is open. The same AddItem dialog, flipped by its
    // `item` prop, so the two can never drift into different field sets.
    const [editing, setEditing] = useState(null);
    // Whether the next classify run also re-judges rows that already carry
    // facets. Reset every time the dialog opens: re-running paid-for rows is a
    // choice to make each time, never a sticky default.
    const [confirmRejudge, setConfirmRejudge] = useState(false);
    // The theme a new child is being added under, and the themes the user has
    // collapsed. Collapsed rather than expanded: everything the filters
    // selected is on screen until the user chooses to fold it away, so no row
    // is hidden by a default nobody set.
    const [addUnder, setAddUnder] = useState(null);
    const [collapsed, setCollapsed] = useState([]);
    const [planning, setPlanning] = useState(null);
    const [toast, setToast] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [items, setItems] = useState([]);
    const [tabCounts, setTabCounts] = useState({ topic: 0, keyword: 0, page: 0, faq: 0 });
    const [countsTick, setCountsTick] = useState(0);
    const [invTick, setInvTick] = useState(0);
    const [loading, setLoading] = useState(true);
    const [runReport, setRunReport] = useState(null);   // the last classification run, in full
    // A report describes one site's bank, so switching sites drops it rather
    // than leaving numbers on screen that are about somewhere else.
    useEffect(() => { setRunReport(null); }, [siteId]);
    const tcfg = TYPES.find(t => t[0] === type);
    const noun = tcfg[1].toLowerCase().replace(/s$/, "");
    const nOf = (n) => n + " " + (n === 1 ? noun : noun + "s");
    const statusOpts = TB_STATUS.filter(([id]) => TB_OUT[type][id]);
    // The toast rule already exists in pipeline.css; a second one under another
    // name would be the same declaration twice.
    const showToast = (msg, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 3000); };
    const toggle = (id) => setSel(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
    const switchTo = (id) => { setType(id); setStat("all"); setCovFilter("all"); setCatFilter("all"); setFitView("keep"); setSel([]); setBulkStat("new"); setCollapsed([]); };

    const load = useCallback(async () => {
      if (siteId == null) { setItems([]); setLoading(false); return; }
      setLoading(true);
      try {
        const r = await api(bankUrl(type));
        setItems(bankRows(r).map(x => mapItem(type, x)));
        // The market block when the route sent one, and null when it did not,
        // so the control cannot keep showing a previous read's answer.
        setMarketAnswer(r && !Array.isArray(r) && r.market ? r.market : null);
      } catch { setItems([]); setMarketAnswer(null); }
      finally { setLoading(false); }
    }, [siteId, type, bankUrl]);
    useEffect(() => { load(); }, [load]);

    // The coverage map for the open tab. A read that fails must never settle
    // into an empty map: "we could not ask" and "the site covers none of this"
    // are opposite answers, and only one of them means write more.
    const loadCov = useCallback(async () => {
      if (siteId == null) { setCov({ loading: false, error: null, data: null }); return; }
      setCov(c => ({ ...c, loading: true, error: null }));
      try { setCov({ loading: false, error: null, data: await api(`/coverage?site_id=${siteId}&kind=${type}`) }); }
      catch (e) { setCov({ loading: false, error: e.message || "Could not read the coverage map.", data: null }); }
    }, [siteId, type]);
    useEffect(() => { loadCov(); }, [loadCov]);

    // What a run would classify INTO, read before anything is spent. A database
    // read, so it is cheap enough for mount. The states stay apart, per facet: a
    // read that failed, a server that answered without that facet at all, and a
    // site that genuinely has none of it. Only an empty CATEGORY vocabulary
    // means the run would invent its own buckets.
    const [vocab, setVocab] = useState({ loading: true, error: null, data: null });
    const loadVocab = useCallback(async () => {
      if (siteId == null) { setVocab({ loading: false, error: null, data: null }); return; }
      setVocab(v => ({ ...v, loading: true, error: null }));
      try { setVocab({ loading: false, error: null, data: readVocab(await api(`/classify/vocabulary?site_id=${siteId}`)) }); }
      catch (e) { setVocab({ loading: false, error: e.message || "Could not read what this site classifies into.", data: null }); }
    }, [siteId]);
    useEffect(() => { loadVocab(); }, [loadVocab]);

    // The route to the vocabulary maker, and null when this screen was mounted
    // without a navigator. Null is passed down as it is: what depends on it
    // renders the destination in words rather than a button that would do
    // nothing.
    const goTaxonomy = go ? () => go(TAXONOMY_SCREEN) : null;

    useEffect(() => {
      if (siteId == null) return; let cancel = false;
      // The same read as the list, so a tab count and the list under it are
      // answers to one question. The Pages tab is not market keyed and the
      // route ignores the parameter there, which is the exclusion
      // lib/market-scope.js states: a page is served at one address by one
      // section, which is the section dimension.
      Promise.all(TYPES.map(([t]) => api(bankUrl(t)).then(r => bankRows(r).length).catch(() => 0)))
        .then(ns => { if (!cancel) setTabCounts({ topic: ns[0], keyword: ns[1], page: ns[2], faq: ns[3] }); });
      return () => { cancel = true; };
    }, [siteId, countsTick, bankUrl]);
    // Adding and deleting move the tab counts as well as the visible list, so
    // every write refreshes both. The discovery signals are computed from the
    // same rows, so a write that leaves them stale would keep a card offering
    // work that is already done.
    const refresh = useCallback(async () => { await Promise.all([load(), loadCov()]); setCountsTick(t => t + 1); setInvTick(t => t + 1); }, [load, loadCov]);

    const del = async (id) => {
      if (siteId == null) return;
      try {
        const r = await api(`/${ENDPOINT[type]}/${id}?site_id=${siteId}`, { method: "DELETE" });
        await refresh();
        // Deleting a theme does not delete what was filed under it: the server
        // promotes those rows to the top level. Said out loud, because a user
        // who expected them to go with it needs to know they are still in the
        // bank, and one who feared losing them needs to know they were not.
        const n = Number(r && r.promoted_children) || 0;
        if (n) showToast(`Deleted. ${n === 1 ? "The topic filed under it is" : `The ${n} topics filed under it are`} now at the top level.`);
      } catch (e) { showToast(e.message || "Could not delete it.", false); }
    };

    // Move one topic under another theme, or to the top level (parent_id null).
    // Every rule the move can break is the server's, and its refusals are
    // sentences, so what comes back is shown as it came.
    const move = async (id, parentId) => {
      if (siteId == null) return;
      setBusy(true);
      try {
        await api(`/${ENDPOINT[type]}/${id}?site_id=${siteId}`, { method: "PATCH", body: JSON.stringify({ parent_id: parentId }) });
        await refresh();
        showToast(parentId == null ? "Moved to the top level." : "Filed under the new theme.");
      } catch (e) { showToast(e.message || "Could not move it.", false); }
      finally { setBusy(false); }
    };

    /* A person overruling the classifier, which is the whole point of marking
       rather than deleting.

       POST /classify/fit stamps fit_source = 'human' and every AI run skips a
       row carrying it, so the correction is durable: the next run leaves the
       row alone instead of quietly putting its own verdict back. "Not judged"
       clears the verdict entirely and hands the row back to the classifier,
       which is the honest thing to do with one a person cannot decide either.

       Optimistic, then re-read from the server on failure, the way `move`
       already is: the toast is what says whether it landed. The reason is
       cleared with the verdict, because the sentence on the row was the
       classifier's argument for a verdict that no longer stands. */
    const setFit = async (it, val) => {
      const next = val === "none" ? null : val;
      if (next === it.fit && (next === null || it.fitByHand)) return;
      setItems(list => list.map(x => (x.id === it.id ? { ...x, fit: next, fitWhy: null, fitByHand: next != null } : x)));
      if (siteId == null) return;
      try {
        await api(`/classify/fit`, { method: "POST", body: JSON.stringify({ site_id: siteId, kind: type, ids: [it.id], fit: next }) });
        showToast(next
          ? `"${it.title}" is ${FIT_LABEL[next].toLowerCase()}. A classify run will not overwrite a verdict you set.`
          : `Verdict cleared: "${it.title}" is unjudged again, so the next classify run will judge it.`);
      } catch (e) {
        showToast(e.message || "That verdict could not be saved.", false);
        load();
      }
    };

    /* A person asserting coverage the scan could not prove, and the reverse.

       POST /coverage/manual writes a manual match, which outranks every
       derived rule and survives rescans, so "I know this is covered" sticks
       the way a hand-set fit verdict does. When the map already points at a
       page (a likely match, inferred from a title or URL), that page's id and
       URL ride along, so confirming an inference records WHICH page was
       confirmed rather than only that something was. The map is re-read
       afterwards instead of patched locally: what the row then shows is the
       server's answer, not this screen's expectation of it. */
    const markCovered = async (it) => {
      if (siteId == null) { showToast("No active site.", false); return; }
      const entry = covBy.get(Number(it.id));
      const best = entry && Array.isArray(entry.posts) ? entry.posts[0] : null;
      try {
        await api(`/coverage/manual`, { method: "POST", body: JSON.stringify({
          site_id: siteId, kind: type, ref_id: it.id,
          post_id: best && best.post_id != null ? best.post_id : undefined,
          url: (best && best.post_url) || undefined,
        }) });
        await loadCov();
        showToast(`"${it.title}" is marked covered by hand. Rescans leave it alone until you unmark it.`);
      } catch (e) { showToast(e.message || "Could not mark it covered.", false); }
    };
    const unmarkCovered = async (it) => {
      if (siteId == null) { showToast("No active site.", false); return; }
      try {
        await api(`/coverage/manual?site_id=${siteId}&kind=${type}&ref_id=${it.id}`, { method: "DELETE" });
        await loadCov();
        showToast(`Unmarked. What the row shows now is the scan's own answer again.`);
      } catch (e) { showToast(e.message || "Could not unmark it.", false); }
    };

    // The edge from this bank to the Planner. The row button opens the preview
    // rather than writing immediately: the write it leads to creates the plan
    // and its first piece together, and what that piece will hold is the thing
    // the user is actually deciding. The backend derives in_use from the edges
    // the plan records, so the row's status is only true after the reload.
    const plan = (it) => {
      if (siteId == null) { showToast("No active site.", false); return; }
      setPlanning(it);
    };

    // AI classification (POST /api/classify/topics). Whoever calls this owns the
    // ids: the header button sends the rows it counted as "New", and the
    // discovery strip sends the ids its own headline counted, so in both cases
    // what the count promises is what runs. With no list, the visible New rows
    // are the batch, which is the header button's original behaviour. The route
    // caps the batch and returns what it left, what it wrote and what the
    // classifier never reached, and all three are reported rather than rounded
    // into a success.
    //
    // The outcome goes to a report that stays on screen rather than a toast that
    // fades, because the vocabulary the run applied is part of the outcome and a
    // run into 34 invented buckets has to be visibly different from a run into
    // the site's own categories afterwards, not only for three seconds.
    const classify = async (idList, runOpts = {}) => {
      if (siteId == null) { showToast("No active site.", false); return; }
      // rejudge re-runs rows that already carry facets. With no id list it is
      // deliberately sent WITHOUT ids: restricting it to the rows this screen
      // counted as New would exclude exactly the rows it exists to re-run. A
      // signal that named its ids keeps them, re-judged or not.
      const rejudge = !!runOpts.rejudge;
      const ids = (idList && idList.length) ? idList : (rejudge ? [] : items.filter(i => i.status === "new").map(i => i.id));
      // Asked for nothing. Nothing was sent, nothing failed, and either word
      // alone would be wrong, so it gets its own outcome.
      if (!ids.length && !rejudge) { setRunReport({ empty: "No topic in this set is missing its facets, so nothing was sent to the AI.", classified: 0 }); return; }
      setBusy(true);
      try {
        const r = await api(`/classify/topics`, { method: "POST", body: JSON.stringify({
          site_id: siteId,
          ...(ids.length ? { ids } : {}),
          ...(rejudge ? { rejudge: true } : {}),
        }) });
        await refresh();
        // Named as topics rather than by the open tab: this route only ever
        // writes to the topics bank, and the strip can fire it from any tab.
        setRunReport({
          error     : null,
          classified: Number(r && r.classified) || 0,
          skipped   : Number(r && r.skipped) || 0,
          remaining : Number(r && r.remaining) || 0,
          // A re-judge run without ids does not know its own size until the
          // server answers, so the server's count is preferred and the sum of
          // what it reported is the fallback: never a number invented here.
          asked     : ids.length || numOr(r && r.asked) || ((Number(r && r.classified) || 0) + (Number(r && r.skipped) || 0) + (Number(r && r.remaining) || 0)),
          // What the RUN says it classified into, not what this screen read
          // earlier. null means the server did not report one, which the report
          // then says nothing about rather than calling it an empty vocabulary.
          vocab     : readVocab(r),
          // The belonging half of the same run, straight from the response and
          // never reconstructed here. A server that did not report it leaves
          // this null, and the report stays silent instead of claiming a sweep.
          fit       : (r && r.fit) || null,
        });
        // Whether a run can add to what the next one sees is the server's
        // business, so the copy on screen is re-read rather than assumed.
        loadVocab();
      } catch (e) {
        // The server writes its errors for the user (an unreadable reply, a
        // missing API key, a batch too big to come back whole), so they are
        // shown verbatim and stay until dismissed.
        setRunReport({ error: e.message || "Classification failed.", classified: 0 });
      } finally { setBusy(false); }
    };

    // Promote a whole card's worth of rows into plans, one plan per row, through
    // the same assembling path as the row button. The card only carries four
    // sample labels, so the titles are read back from the bank: a plan named
    // from a guess would be a plan named wrongly.
    //
    // There is no per-row confirmation here, so the reporting carries the whole
    // weight. Three different numbers, because they are three different facts:
    // how many plans now exist, how many of their pieces can actually generate a
    // draft, and how many could not be assembled and why. A seed that cannot be
    // assembled is still promoted on its own, which is exactly what this button
    // did before, and it is counted separately rather than folded into a total
    // that would read as if the assembly had worked.
    const planMany = async (sig, kind, endpoint) => {
      if (siteId == null) { showToast("No active site.", false); return; }
      const ids = sig.ids || [];
      if (!ids.length) return;
      let rows;
      try { rows = await api(`/${endpoint}?site_id=${siteId}`); }
      catch (e) { showToast(e.message || "Could not read the bank.", false); return; }
      const byId = new Map((Array.isArray(rows) ? rows : []).map(r => [Number(r.id), r]));
      const targets = ids.map(id => byId.get(Number(id))).filter(Boolean);
      if (!targets.length) { showToast("Those rows are no longer in the bank. Refresh and try again.", false); return; }

      setBusy(true);
      let done = 0, canGenerate = 0, solo = 0, failure = null;
      let why = NO_FACETS[kind] || null;
      for (const t of targets) {
        const it = { id: Number(t.id), title: labelOf(kind, t) };
        try {
          // The preview is free and writes nothing, so asking per row costs
          // nothing, and it is the only way to know whether the piece will have
          // a keyword. A preview that fails to read is not a hard failure: it
          // means this run does not know what it would have pulled, so the row
          // falls back to the single promotion and the reason is reported.
          let ok = false;
          if (!NO_FACETS[kind]) {
            try {
              const pv = await api(`/content-plans/assemble-preview?site_id=${siteId}&kind=${kind}&ref_id=${it.id}`);
              ok = !!(pv && pv.ok);
              if (!ok && pv && pv.reason && !why) why = pv.reason;
            } catch (e) { if (!why) why = e.message || "the preview could not be read"; }
          }
          if (ok) {
            const res = await createAssembled(siteId, kind, it);
            done++;
            // Counted on what came back, not on what the preview promised.
            if (res && res.piece) { if (pieceCanGenerate(res.piece)) canGenerate++; }
            else { solo++; if (!why) why = noneReason(res); }
          } else {
            await createSingle(siteId, kind, it);
            done++; solo++;
          }
        } catch (e) { failure = e.message || "the request failed"; break; }
      }
      setBusy(false);
      await refresh();
      if (failure) { showToast(`Created ${done} of ${targets.length}, then stopped: ${failure}`, false); return; }
      const missing = ids.length - targets.length;
      const bits = [`Created ${done} plan${done === 1 ? "" : "s"}`, `${canGenerate} can generate a draft`];
      if (solo) bits.push(`${solo} could not be assembled and ${solo === 1 ? "holds" : "hold"} only its own row: ${why || "no facets to match on"}`);
      if (missing) bits.push(`${missing} row${missing === 1 ? " was" : "s were"} gone from the bank`);
      showToast(bits.join(", "), !solo && !missing);
    };

    // What the discovery strip may do from here, keyed by signal so each card
    // gets only the action that is right for it.
    //
    // faqs-free is deliberately absent. A plan per FAQ is a page per question,
    // which is not how FAQs get used: an approved answer wants attaching to a
    // page that already exists, and this screen has no attach UI. That card
    // still reports what is sitting there, with no button, rather than offering
    // a bulk action that would be the wrong unit of work.
    const discoveryActions = {
      classify: (sig) => classify(sig.ids),
      "topics-free": (sig) => planMany(sig, "topic", "research/topics"),
      "gaps-open"  : (sig) => planMany(sig, "gap", "todos"),
      // An 'open' action: the card's work is a VIEW, not a write. switchTo
      // first, because it resets the filters for the new tab, and the duplicate
      // filter is then set on top of that reset; pressed from the Topics tab it
      // simply re-lands there with the filter on.
      "topics-duplicated": () => { switchTo("topic"); setCovFilter("duplicate"); },
    };

    const copyQA = async (it) => {
      try {
        if (!navigator.clipboard) throw new Error("This browser will not give the page clipboard access.");
        await navigator.clipboard.writeText([it.title, it.detail].filter(Boolean).join("\n\n"));
        showToast("Q&A copied");
      } catch (e) { showToast(e.message || "Could not copy it.", false); }
    };

    // One request per row: no bank takes a bulk write. Stopping at the first
    // failure keeps the count reported in the toast true.
    const runBulk = async (fn, past) => {
      if (siteId == null || !sel.length) return;
      setBusy(true);
      let done = 0, failure = null;
      for (const id of sel) {
        try { await fn(id); done++; }
        catch (e) { failure = e.message || "the request failed"; break; }
      }
      setBusy(false);
      await refresh();
      if (failure) { showToast(`${past} ${done} of ${sel.length}, then stopped: ${failure}`, false); return; }
      setSel([]); setSelectMode(false);
      showToast(`${past} ${nOf(done)}`);
    };
    const bulkStatus = () => {
      const dbStat = TB_OUT[type][bulkStat];
      if (!dbStat) { showToast(`A ${noun} has no "${bulkStat}" status.`, false); return; }
      runBulk(id => api(`/${ENDPOINT[type]}/${id}?site_id=${siteId}`, { method: "PATCH", body: JSON.stringify({ status: dbStat }) }), "Updated");
    };
    const bulkDelete = () => runBulk(id => api(`/${ENDPOINT[type]}/${id}?site_id=${siteId}`, { method: "DELETE" }), "Deleted");

    const counts = { all: items.length }; TB_STATUS.forEach(([id]) => counts[id] = items.filter(i => i.status === id).length);
    const unclassified = items.filter(i => i.status === "new").length;
    // The same four answers the dialog gives for the facet that decides whether
    // a run is worth anything, short enough to hover. It never says "no
    // categories" for a read that failed or for a facet the server did not
    // report. Tags are left to the dialog: a hover is not the place for the
    // weaker of the two.
    const catVocab = (vocab.data || {}).categories;
    const vocabTitle = vocab.loading ? "Reading which categories this site classifies into"
      : vocab.error ? `Could not read what this site classifies into: ${vocab.error}`
      : facetState(catVocab) === "some" ? `Classifies into the ${plural(catVocab.total, "category", "categories")} this site already uses`
      : facetState(catVocab) === "none" ? "This site has no categories yet, so a run would invent a bucket per topic"
      : facetState(catVocab) === "failed" ? "Could not read which categories this site classifies into"
      : "The server did not say which categories this site classifies into";

    // The coverage map, by bank id. `duplicates` is merged in on top of `items`
    // because the API caps each list at 60 separately and computes `duplicates`
    // over the FULL set, so a duplicated row past the items ceiling is still
    // known to be duplicated.
    const covData = cov.data;
    const covBy = new Map();
    for (const e of (covData && Array.isArray(covData.items) ? covData.items : [])) covBy.set(Number(e.ref_id), e);
    for (const e of (covData && Array.isArray(covData.duplicates) ? covData.duplicates : [])) covBy.set(Number(e.ref_id), e);
    const covCapped = !!(covData && covData.capped);

    const covStateOf = (id) => {
      if (cov.error) return "unknown";
      if (!covData) return cov.loading ? "loading" : "unknown";
      if (covData.never_scanned) return "unscanned";
      const hit = covBy.get(Number(id));
      if (hit) return hit.posts.length > 1 ? "duplicate" : (isProof(bestRule(hit.posts)) ? "covered" : "likely");
      // Past the API's ceiling the map simply did not answer for this row.
      // Calling that "not covered" is the one error that costs the user work.
      return covCapped ? "unknown" : "none";
    };

    const neverScanned = !!(covData && covData.never_scanned);
    const covUsable = !cov.error && !!covData && !neverScanned;

    const covCounts = { all: items.length, covered: 0, none: 0, duplicate: 0 };
    for (const i of items) {
      const s = covStateOf(i.id);
      if (s === "none") covCounts.none++;
      else if (s === "duplicate") { covCounts.duplicate++; covCounts.covered++; }
      else if (s === "covered" || s === "likely") covCounts.covered++;
    }
    // A row whose coverage is unknown answers neither "covered" nor "not
    // covered", so it belongs only under All rather than being assigned to one.
    const covMatches = (i) => {
      // With no usable map the filter row is not rendered, so an old selection
      // must not silently keep hiding rows behind a control nobody can see.
      if (!covUsable || covFilter === "all") return true;
      const s = covStateOf(i.id);
      if (covFilter === "covered") return s === "covered" || s === "likely" || s === "duplicate";
      if (covFilter === "none") return s === "none";
      return s === "duplicate";
    };
    // Fit filters LAST, and the count of what it removed is kept, because that
    // number has to be on screen. Counted against the rows the other filters
    // already allowed, so it says how many THIS VIEW is hiding rather than how
    // many the bank holds. On a bank with no fit column there is nothing to
    // filter and nothing to claim, so the rows pass through untouched.
    const fitBank = FIT_BANKS.includes(type);
    // The categories the loaded rows actually carry, as filter options. Topics
    // only: it is the one bank here whose rows hold the facet. The option list
    // is the rows' own distinct values, so a category nothing holds is never
    // offered as a filter that would show an empty list.
    const catValues = type === "topic" ? [...new Set(items.map(i => i.category).filter(Boolean))].sort((a, b) => a.localeCompare(b)) : [];
    const catMatches = (i) => type !== "topic" || catFilter === "all"
      || (catFilter === "__none__" ? !i.category : i.category === catFilter);
    const passed = items.filter(i => (stat === "all" || i.status === stat) && covMatches(i) && catMatches(i));
    const rows = fitBank ? passed.filter(i => fitMatches(i, fitView)) : passed;
    const hiddenByFit = passed.length - rows.length;
    // How much of this bank has ever been judged. A screen offering a fit
    // filter over rows nothing has looked at would be offering a filter on an
    // empty question, so it says which it is.
    const judged = fitBank ? items.filter(i => i.fit).length : 0;

    // The tree, built from the rows the filters already chose, so nothing here
    // can add a row the filters excluded or drop one they kept. `kidTotals` is
    // counted over the WHOLE bank instead, because "2 of 5 under this one
    // match" is the sentence that stops a filtered theme reading as a complete
    // one, and the other 3 are only knowable from the unfiltered list.
    const treeView = type === "topic";
    const kidTotals = new Map();
    if (treeView) for (const i of items) if (i.parent_id != null) kidTotals.set(i.parent_id, (kidTotals.get(i.parent_id) || 0) + 1);
    const branches = treeView
      ? groupTree(rows, items)
      : rows.map(r => ({ item: r, kids: [], awayFrom: null }));
    // Where a topic may be filed. Two levels, so only a top-level topic can
    // take children, and one that already holds some can only sit at the top
    // itself. Both are the server's rules (backend/lib/topic-tree.js), mirrored
    // here so the select offers the legal moves rather than collecting
    // refusals. The server still decides: this is the shape of the control, not
    // the rule.
    const parentOpts = treeView ? items.filter(i => i.parent_id == null) : [];
    const isOpen = (id) => !collapsed.includes(id);
    const toggleOpen = (id) => setCollapsed(c => (c.includes(id) ? c.filter(x => x !== id) : [...c, id]));

    const dupTotal = (covData && covData.duplicates_total) || 0;
    const dupShown = (covData && Array.isArray(covData.duplicates)) ? covData.duplicates.length : 0;

    /**
     * One row, wherever it sits in the tree.
     *
     * A child renders exactly like a theme: the same badges, the same coverage,
     * the same actions, the same selection checkbox, so bulk actions and Plan
     * reach every row whatever its level. Only the indent, the expander and the
     * two tree controls differ, and a second copy of this card would be a
     * second place for the rest to drift.
     */
    const row = (it, b) => {
      const covState = covStateOf(it.id);
      const covEntry = covBy.get(Number(it.id));
      // Hand-marking is offered exactly where the map's answer is weaker than
      // the person's: no coverage found, or coverage only inferred. A row the
      // scan can PROVE needs no assertion, and a row the map could not answer
      // for (unknown, unscanned) has nothing on screen worth contradicting.
      const handMarked = covEntry ? bestRule(covEntry.posts) === "manual" : false;
      // A manual coverage row is identified by the POST it confirms (the
      // server requires post_id: the row's identity in content_coverage), so
      // hand-marking is only offered where the map already points at a page.
      // A "not covered" row names no page, and asserting "covered by
      // something, I won't say what" is not a record the scan could protect.
      const covHasPost = !!(covEntry && Array.isArray(covEntry.posts) && covEntry.posts[0] && covEntry.posts[0].post_id != null);
      const canHandMark = !handMarked && covState === "likely" && covHasPost;
      const shownKids = (b.kids || []).length;
      const totalKids = kidTotals.get(it.id) || 0;
      const hiddenKids = totalKids - shownKids;
      // Two levels: only a top-level topic can hold articles, and a topic that
      // already holds some has nowhere to go but the top.
      const canHoldChildren = treeView && !b.child && it.parent_id == null;
      const movable = treeView && totalKids === 0;
      const card = (
        <div className="bi-card" style={{ cursor: "default", flexDirection: "column", gap: 8 }}>
          <div className="bi-card-head">
            <div className="bi-card__name" style={{ fontSize: "var(--text-sm)", display: "flex", alignItems: "center", gap: 6 }}>
              {shownKids > 0 && (
                <button className="bi-iconbtn" title={isOpen(it.id) ? "Fold this theme away" : `Show the ${shownKids} under this one`} onClick={() => toggleOpen(it.id)}>
                  <I name={isOpen(it.id) ? "chevron-down" : "chevron-right"} size={16} />
                </button>
              )}
              {it.title}
              {/* The count is what a collapsed theme is judged on, so it says
                  how many are on screen AND how many exist when a filter has
                  taken some out. One number for both would be a promise the
                  expander cannot keep. */}
              {totalKids > 0 && (
                <span className="bi-badge bi-badge--archived"
                      title={hiddenKids > 0 ? `${hiddenKids} more ${hiddenKids === 1 ? "is" : "are"} filed under this one but not in this view.` : "Topics filed under this one"}>
                  {hiddenKids > 0 ? `${shownKids} of ${totalKids} under it` : `${totalKids} under it`}
                </span>
              )}
            </div>
            <div className="bi-card-badges">
              {covBadge(covState, covEntry, cov.error ? `Could not read the coverage map: ${cov.error}` : null)}
              {statusTag(it.status)}
              {/* Editable where it is shown, because a verdict has to be
                  arguable on the row it is about. The hover carries the one
                  sentence the classifier gave for it, and says when the verdict
                  is one you set. */}
              {fitBank && (
                <div title={fitTitle(it, noun)}>
                  <FD label="Fit" value={it.fit || "none"} opts={FIT_OPTS} colorOf={v => FIT_COLOR[v] || FIT_COLOR.none} down onChange={val => setFit(it, val)} />
                </div>
              )}
              {/* The facets a classify run wrote, which this register stored
                  and never showed: the tokens were spent, so the result is on
                  the row. The card has no column headers, so each chip's hover
                  says WHICH facet it is; absent facets render nothing rather
                  than an empty chip. */}
              {type === "topic" && it.category && <span className="cluster-tag" title={`Category: the bucket a classify run filed this topic in. Facet assembly pulls material into a piece by shared category, so this is what connects it to the rest of the banks.`}>{it.category}</span>}
              {type === "topic" && it.format && <span className="cluster-tag" title={`Format: the shape of content the classifier judged this topic wants (guide, comparison, how-to and so on).`}>{it.format}</span>}
              {type === "topic" && it.intent && <span className="research-tag research-tag--intent" title={`Intent: why a reader would search for this, as the classifier judged it. Edit the row to correct it.`} style={{ textTransform: "none" }}>{capOf(it.intent)}</span>}
              {it.tags.map(t => <span className="research-tag" key={t} style={{ textTransform: "none" }}>{t}</span>)}
            </div>
          </div>
          <p className="research-row-desc" style={{ margin: 0 }}>{it.detail}</p>
          {/* Selected by the filters, but its theme was not, so it is here at
              the top rather than nowhere. */}
          {b.awayFrom && (
            <div className="ds-card__note">
              <I name="arrow-right" size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              {b.awayFrom.title
                ? <>Filed under <b>{b.awayFrom.title}</b>, which this view does not show, so it is listed at the top.</>
                : <>Filed under a topic that is no longer in the bank, so it is listed at the top.</>}
            </div>
          )}
          {covEntry && covEntry.posts.length > 0 && (
            <div className="bi-card__meta">
              {covEntry.posts.length > 1 && <span style={{ fontSize: 11, color: "var(--warning)", fontWeight: 600 }}>These pages compete for it:</span>}
              {covEntry.posts.map(p => <CoveringPost key={p.post_id} p={p} />)}
            </div>
          )}
          <div className="bi-card-foot" style={{ paddingTop: 10 }}>
            {/* `from` already reads as a sentence ("via competitor Acme"), so
                nothing is prefixed onto it: "from via" was two prepositions
                claiming one fact. */}
            <span className={"tb-src" + (it.from ? "" : " tb-src--manual")}><I name={it.from ? "sparkles" : "edit"} size={12} />{it.from ? it.from : "added manually"} · {it.when}</span>
            <div className="bi-card-foot__right">
              {treeView && (
                <select
                  className="hds-select" style={{ maxWidth: 180, fontSize: 12 }}
                  value={it.parent_id == null ? "" : String(it.parent_id)}
                  disabled={busy || !movable}
                  title={movable
                    ? "File this topic under a theme, or move it back to the top level."
                    : `This theme holds ${totalKids} ${totalKids === 1 ? "topic" : "topics"}, and topics go ${treeDepth} levels deep, so it can only sit at the top. Move them out first.`}
                  onChange={e => move(it.id, e.target.value === "" ? null : Number(e.target.value))}>
                  <option value="">Top level</option>
                  {parentOpts.filter(p => p.id !== it.id).map(p => <option key={p.id} value={String(p.id)}>{p.title}</option>)}
                </select>
              )}
              {canHoldChildren && <button className="bi-btn bi-btn--sm" onClick={() => setAddUnder(it)}><I name="plus" size={14} />Add child topic</button>}
              {/* Offered on themes only, which is the depth cap made visible:
                  a topic that is already a child cannot take children, and the
                  server refuses that by name if it is asked anyway. */}
              {canHoldChildren && <button className="bi-btn bi-btn--sm bi-btn--ai" title={`Propose more topics to file under "${it.title}", from a keyword, a category or a tag this site already owns.`} onClick={() => setGrowing({ parent: it })}><I name="shuffle" size={14} />Grow</button>}
              <button className="bi-btn bi-btn--sm" disabled={planning != null} onClick={() => plan(it)}><I name="arrow-right" size={14} />Plan</button>
              {(type === "topic" || type === "keyword") && <button className="bi-btn bi-btn--sm bi-btn--ai" onClick={() => onWrite && onWrite(it.title)}><I name="edit" size={14} />Write</button>}
              {/* A page row's Write carries its own id as the second argument:
                  the shell links the saved draft back to this row (status
                  drafted, linked_post_id), which is how a tracking register
                  learns its page got written. Topic and keyword rows keep the
                  one-argument call they always made. */}
              {type === "page" && <button className="bi-btn bi-btn--sm bi-btn--ai" title="Draft this page in Write. The saved draft is linked back to this row, which then reads as drafted." onClick={() => onWrite && onWrite(it.title, { pageId: it.id })}><I name="edit" size={14} />Write</button>}
              {type === "faq" && <button className="bi-btn bi-btn--sm" onClick={() => copyQA(it)}><I name="copy" size={14} />Copy Q&amp;A</button>}
              {canHandMark && <button className="bi-iconbtn" title={`Mark this ${noun} covered by hand. A hand mark outranks what the scan inferred and survives rescans, until you unmark it.${covState === "likely" ? " The page the scan pointed at is recorded as the one you confirmed." : ""}`} onClick={() => markCovered(it)}><I name="check-circle" size={16} /></button>}
              {handMarked && <button className="bi-iconbtn" title="Marked covered by hand. Press to unmark and hand the answer back to the scan." onClick={() => unmarkCovered(it)}><I name="circle" size={16} /></button>}
              {/* Keywords are the one bank without an Edit here: the update
                  route carries no keyword text, and the Keywords Planner is
                  that bank's editing surface. */}
              {type !== "keyword" && <button className="bi-iconbtn" title={`Edit this ${noun}`} onClick={() => setEditing(it)}><I name="edit" size={16} /></button>}
              <button className="bi-iconbtn" title={totalKids > 0 ? `Delete. The ${totalKids} ${totalKids === 1 ? "topic" : "topics"} filed under this one stay in the bank, at the top level.` : "Delete"} onClick={() => del(it.id)}><I name="trash" size={16} /></button>
            </div>
          </div>
        </div>
      );
      const indent = b.child ? { marginLeft: 28 } : null;
      if (!selectMode) return <div key={it.id} style={indent}>{card}</div>;
      return (
        <div key={it.id} style={indent} className={"bi-select-wrap bi-select-wrap--on" + (sel.includes(it.id) ? " bi-select-wrap--selected" : "")}>
          <label className="hds-check bi-select-wrap__check"><input type="checkbox" checked={sel.includes(it.id)} onChange={() => toggle(it.id)} /><span className="hds-check__box"><I name="check" size={12} /></span></label>
          {card}
        </div>
      );
    };

    return (
      <div className="bi-page">
        <div className="bi-header">
          <div className="bi-header__titles"><h1 className="bi-title"><span className="bi-title__icon"><I name="layers" size={20} /></span>Topics Bank</h1></div>
          <div className="bi-header-actions">
            <button className="bi-btn" onClick={() => setAdding(true)}><I name="plus" size={16} />Add {noun}</button>
            {/* The only control that makes the bank WIDER rather than tidier.
                Topics alone: it writes topics, so offering it from the Pages or
                FAQ tab would be a button that fills a bank you are not looking
                at. */}
            {type === "topic" && <button className="bi-btn bi-btn--ai" title="Propose more topics from a keyword, a category or a tag this site already owns. Nothing is added until you keep it." disabled={busy} onClick={() => setGrowing({ parent: null })}><I name="shuffle" size={16} />Grow topics</button>}
            {type === "topic" && unclassified > 0 && <button className="bi-btn bi-btn--ai" title={vocabTitle} disabled={busy} onClick={() => { setConfirmRejudge(false); setConfirm("classify"); }}><I name="sparkles" size={16} />{busy ? "Classifying..." : `Classify (${unclassified})`}</button>}
            <button className="bi-btn" onClick={() => setSyncing(true)}><I name="globe" size={16} />Sync coverage</button>
            <button className="bi-btn" onClick={() => { setSelectMode(s => !s); setSel([]); }}>{selectMode ? <><I name="x" size={16} />Cancel</> : <><I name="check-square" size={16} />Select</>}</button>
            <button className="bi-btn" onClick={refresh}><I name="refresh" size={16} />Refresh</button>
          </div>
        </div>
        <p className="research-intro">Every {noun} this site has: published, planned, or untouched. <b>Status</b> is where it sits in the pipeline here; <b>coverage</b> is whether the live site already says it, which is how you see duplication before commissioning more. Keywords feed the <b>Keywords Planner</b>; gaps go to <b>To-do</b>.</p>

        {/* THE MARKET CONTROL, above the tabs because it governs every one of
            them that is a register. Draws nothing on a website with fewer than
            two markets, which is most of them.
            The Pages tab is NOT market keyed and the control says so below:
            `content_pages` carries no market_id because a page is served at one
            address by one section, which is the section dimension
            (lib/market-scope.js states the exclusion and the reason). */}
        {MarketControl && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
            <MarketControl markets={markets} marketId={marketId} onPick={onPickMarket}
              answer={marketAnswer} noun={noun} />
            {type === "page" && Array.isArray(markets) && markets.length > 1 && (
              <div className="ds-strip ds-strip--quiet">
                <I name="info" size={14} />
                <span>
                  The Pages tab is not one of the market registers, so the market above does not filter it. A page is
                  served at one address by one section, which makes it a question about the section rather than about
                  the market: pick the section in the switcher at the top.
                </span>
              </div>
            )}
          </div>
        )}

        {window.MlDiscovery && <window.MlDiscovery.DiscoveryStrip siteId={siteId} module="topics" actions={discoveryActions} reloadKey={invTick} />}

        {/* The last classification run, in full. Only after a run: a bank with
            nothing left to classify has nothing to say here, and a standing
            banner would be nagging rather than informing. */}
        {runReport && <ClassifyRunReport run={runReport} onDismiss={() => setRunReport(null)} onTaxonomy={goTaxonomy} />}

        <div className="tb-tabs">
          {TYPES.map(([id, label, icon]) => (
            <button key={id} className={"tb-tab" + (type === id ? " tb-tab--active" : "")} onClick={() => switchTo(id)}>
              <I name={icon} size={15} />{label}<span className="tb-tab__count">{tabCounts[id]}</span>
            </button>
          ))}
        </div>

        {/* Said once, at the top of the tab, because the Plan button on a page
            row will discover it anyway: better the register admits what it is
            before the user builds a workflow on what it is not. */}
        {type === "page" && (
          <div className="bi-hint" style={{ marginBottom: 12 }}>
            Pages do not join facet assembly: this tab is a tracking register of what the site should have. Drafting happens through <b>Write</b> on the row, which links the saved draft back here.
          </div>
        )}

        <div className="bi-stat-cards" style={{ gridTemplateColumns: "repeat(5, 1fr)", maxWidth: 660 }}>
          {[["all", "All", "var(--primary)"], ...TB_STATUS.map(([id, l]) => [id, l, TB_STATUS_COLOR[id]])].map(([id, l, c]) => (
            <button key={id} className={"bi-stat-card bi-stat-card--clickable" + (stat === id ? " bi-stat-card--active" : "")} onClick={() => setStat(id)}>
              <div className="bi-stat-card__top"><span className="bi-stat-card__dot" style={{ background: c }} /><span className="bi-stat-card__label">{l}</span></div>
              <span className="bi-stat-card__value">{counts[id]}</span>
            </button>
          ))}
        </div>

        {/* Coverage. Deliberately its own row under the status cards rather than a
            fifth status: they answer different questions and a merged control
            would make "planned" and "already published" look like one scale. */}
        {cov.error && (
          <div className="ds-strip ds-strip--error" style={{ marginBottom: 12 }}>
            <I name="alert-triangle" size={14} />
            <span>Could not read the coverage map: {cov.error}</span>
            <button className="bi-btn bi-btn--sm" onClick={loadCov}>Try again</button>
          </div>
        )}
        {!cov.error && neverScanned && (
          <div className="bi-hint" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
            <I name="search" size={15} style={{ flex: "none" }} />
            <span style={{ flex: 1 }}>Nothing has read this site yet, so this register cannot say what is already published. <b>Sync coverage</b> reads every published post and page and matches them against the banks.</span>
            <button className="bi-btn bi-btn--sm" onClick={() => setSyncing(true)}>Sync coverage</button>
          </div>
        )}
        {covUsable && (
          <div className="bi-status-filter">
            <span className="bi-status-filter__label">Coverage</span>
            {COV_FILTERS.map(([id, l, why]) => (
              <button key={id} className={"bi-status-chip" + (covFilter === id ? " bi-status-chip--active" : "")} title={why} onClick={() => setCovFilter(id)}>{l}<span className="bi-status-chip__n">{covCounts[id]}</span></button>
            ))}
            {covData.last_synced_at && <span className="ds-card__note">site last read {String(covData.last_synced_at).slice(0, 10)}</span>}
          </div>
        )}
        {!cov.error && covCapped && (
          <div className="bi-hint" style={{ marginBottom: 12 }}>
            The coverage map answers for the first {covData.items.length} of {covData.items_total} covered {noun}s. The rest read as
            <b> coverage unknown</b> rather than uncovered, because this screen was not told either way.
          </div>
        )}
        {!cov.error && dupTotal > 0 && (
          <div className="bi-hint" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
            <I name="alert-triangle" size={15} style={{ flex: "none", color: "var(--warning)" }} />
            <span style={{ flex: 1 }}><b>{dupTotal} {dupTotal === 1 ? noun : noun + "s"}</b> {dupTotal === 1 ? "is" : "are"} covered by more than one published page, which is possible duplication. Each row names the competing pages.{dupShown < dupTotal ? ` The map lists ${dupShown} of them.` : ""}</span>
            <button className="bi-btn bi-btn--sm" onClick={() => setCovFilter("duplicate")}>Show them</button>
          </div>
        )}

        {/* Fit. Its own row, like coverage, and for the same reason: "where
            this sits in the pipeline" and "whether it is this business's at
            all" are different questions, and one control for both would put
            them on one scale. */}
        {fitBank && (
          <div className="bi-status-filter">
            <span className="bi-status-filter__label">Fit</span>
            <FD label="Fit" value={fitView} opts={FIT_VIEW_OPTS} colorOf={v => FIT_COLOR[v] || FIT_COLOR.none} down onChange={setFitView} />
            <span className="ds-card__note">
              {judged === 0
                ? `Nothing here has been judged for belonging yet. Classify ${noun}s to have it decided, or set a verdict on a row.`
                : `${judged} of the ${items.length} ${judged === 1 ? "row has" : "rows have"} a verdict. Set one on a row to overrule the classifier: a verdict you set is never overwritten by a later run.`}
            </span>
          </div>
        )}
        {/* What this view is not showing. It says "hidden" and not "removed"
            deliberately: nothing here deletes a row on a verdict. */}
        {fitBank && fitView === "keep" && hiddenByFit > 0 && (
          <div className="ds-strip ds-strip--quiet">
            <I name="eye" size={14} />
            <span>
              {hiddenByFit} {hiddenByFit === 1 ? `${noun} is` : `${noun}s are`} hidden here as off market or off topic. They are
              still in the bank, and a verdict you disagree with can be changed on the row.
            </span>
            <button className="bi-btn bi-btn--sm" onClick={() => setFitView("all")}>Show them</button>
          </div>
        )}

        {/* Category, its own row like Coverage and Fit: which bucket a classify
            run filed a topic in is a fourth independent question. Rendered only
            when some row carries one, because a filter over a facet nothing
            holds is a control with one empty answer. */}
        {type === "topic" && catValues.length > 0 && (
          <div className="bi-status-filter">
            <span className="bi-status-filter__label">Category</span>
            <FD label="Category" value={catFilter}
              opts={[["all", "Category: all"], ...catValues.map(c => [c, c]), ["__none__", "No category yet"]]}
              colorOf={v => v === "all" ? "var(--text-muted)" : v === "__none__" ? "var(--warning)" : "var(--primary)"}
              down onChange={setCatFilter} />
            <span className="ds-card__note">
              The buckets a classify run filed these topics in. Facet assembly connects rows by shared category, so a topic with none assembles nothing.
              {(() => { const n = items.filter(i => !i.category).length; return n ? ` ${n} ${n === 1 ? "row has" : "rows have"} none yet.` : ""; })()}
            </span>
          </div>
        )}

        {selectMode && (
          <div className="bi-bulk-bar">
            <span className="bi-bulk-bar__count">{sel.length} selected</span>
            <button className="bi-btn bi-btn--sm" onClick={() => setSel(rows.map(i => i.id))}><I name="check-square" size={14} />Select all ({rows.length})</button>
            <button className="bi-btn bi-btn--sm" onClick={() => setSel([])}>Clear</button>
            <span className="bi-bulk-bar__sep" />
            <FD label="Set status" value={bulkStat} opts={statusOpts} colorOf={v => TB_STATUS_COLOR[v]} onChange={setBulkStat} />
            <button className="bi-btn bi-btn--sm" disabled={busy || !sel.length} onClick={bulkStatus}>Set status</button>
            <button className="bi-btn bi-btn--sm bi-btn--danger" disabled={busy || !sel.length} onClick={() => setConfirm("delete")}><I name="trash" size={14} />Delete</button>
          </div>
        )}

        <div className="bi-list--rows">
          {branches.map(b => {
            const shownKids = b.kids.length;
            const open = isOpen(b.item.id);
            return (
              <React.Fragment key={b.item.id}>
                {row(b.item, b)}
                {open && b.kids.map(k => row(k, { child: true, kids: [] }))}
                {/* Collapsed, so the rows are still selected by the filters and
                    still counted above. Said here rather than left to the
                    chevron, because a fold that swallows rows silently is the
                    same as a filter that drops them. */}
                {!open && shownKids > 0 && (
                  <div className="ds-card__note" style={{ marginLeft: 28, marginTop: -4 }}>
                    {shownKids} {shownKids === 1 ? "topic" : "topics"} under this one, folded away.
                  </div>
                )}
              </React.Fragment>
            );
          })}
          {loading ? <div className="bi-subempty">Loading {tcfg[1].toLowerCase()}…</div> : rows.length === 0 && <div className="bi-subempty">No {tcfg[1].toLowerCase()} in this view yet.</div>}
        </div>

        {planning && window.MlBiModals && (
          <PlanPreview
            siteId={siteId} kind={type} item={planning} noun={noun}
            onClose={() => setPlanning(null)}
            onDone={async (msg, ok) => { setPlanning(null); await refresh(); showToast(msg, ok); }} />
        )}
        {syncing && window.MlBiModals && <SyncCoverage siteId={siteId} onClose={() => setSyncing(false)} onDone={refresh} />}
        {/* Growing the bank. The refresh runs on the write rather than on the
            close, so the rows are in the list behind the panel by the time it
            is shut, and a user who closes it without reading the summary still
            sees what landed. */}
        {growing && window.MlBiModals && (
          <GrowTopics
            siteId={siteId} parent={growing.parent} onDepth={setTreeDepth}
            onClose={() => setGrowing(null)}
            onAdded={async (n) => { await refresh(); if (n) showToast(`Added ${nOf(n)} to the bank.`); }} />
        )}
        {adding && window.MlBiModals && <AddItem type={type} siteId={siteId} onClose={() => setAdding(false)} onAdded={async (v) => { setAdding(false); showToast(`Added "${v}"`); await refresh(); }} />}
        {/* The same dialog, editing the row it was opened from. The reload is
            what puts the saved fields on screen, so the toast and the list can
            never say two different things about what was stored. */}
        {editing && window.MlBiModals && (
          <AddItem type={type} siteId={siteId} item={editing}
            onClose={() => setEditing(null)}
            onAdded={async (v) => { setEditing(null); showToast(`Saved "${v}"`); await refresh(); }} />
        )}
        {/* The same dialog, filing what it creates under a theme. A new child
            lands in the bank as new, so it can be caught by a status filter its
            theme does not match: it is then listed at the top saying where it
            belongs, rather than disappearing. */}
        {addUnder && window.MlBiModals && (
          <AddItem type={type} siteId={siteId} parent={addUnder}
            onClose={() => setAddUnder(null)}
            onAdded={async (v) => { const p = addUnder; setAddUnder(null); showToast(`Added "${v}" under "${p.title}"`); await refresh(); }} />
        )}

        {/* What the run costs, and what it will classify INTO, both stated
            before the tokens are spent: a category nothing else shares groups
            nothing, so the vocabulary is the part worth seeing first.
            .ds-brief gives the blocks their rhythm, so none of them carries a
            margin of its own. */}
        {confirm === "classify" && window.MlBiModals && <window.MlBiModals.ConfirmDialog
          title={confirmRejudge ? "Classify the whole bank, re-judging what already has facets?" : `Classify ${unclassified} topic${unclassified === 1 ? "" : "s"}?`}
          body={<div className="ds-brief">
          <div>{confirmRejudge
            ? "Sends the new topics AND re-runs every topic that already carries facets, so the whole bank re-buckets against the current vocabulary. This uses tokens, including on rows that were already paid for once. One run still takes at most 60; the rest stay for the next one."
            : `Sends ${unclassified === 1 ? "it" : "them"} to the AI to get an intent, a format, a category and tags, and moves ${unclassified === 1 ? "it" : "them"} out of New. This uses tokens.${unclassified > 60 ? " One run handles 60; the rest stay for the next one." : ""}`}</div>
          {/* Re-judging is the way a bank classified under an OLD vocabulary
              catches up with the current one. Unchecked by default every time:
              spending tokens twice on the same rows is a choice, not a
              preference to remember. */}
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <span className="hds-check"><input type="checkbox" checked={confirmRejudge} onChange={e => setConfirmRejudge(e.target.checked)} /><span className="hds-check__box"><I name="check" size={12} /></span></span>
            <span>Also re-judge rows already classified</span>
          </label>
          {confirmRejudge && <div className="ds-card__note">Re-judging re-runs rows that already have facets so they re-bucket against the current vocabulary. Those rows cost tokens again. A fit verdict you set by hand is still left alone.</div>}
          <VocabBrief vocab={vocab} onTaxonomy={goTaxonomy} />
        </div>} confirmLabel={confirmRejudge ? "Classify and re-judge" : "Classify"} onClose={() => setConfirm(null)} onConfirm={() => { setConfirm(null); classify(null, { rejudge: confirmRejudge }); }} />}
        {confirm === "delete" && window.MlBiModals && <window.MlBiModals.ConfirmDialog danger title={`Delete ${nOf(sel.length)}?`} body="They leave this bank for good. Content plans already created from them are untouched." confirmLabel="Delete" onClose={() => setConfirm(null)} onConfirm={() => { setConfirm(null); bulkDelete(); }} />}

        {toast && <div className="inbox-toast"><I name={toast.ok ? "check-circle" : "alert-triangle"} size={16} style={toast.ok ? null : { color: "var(--warning)" }} />{toast.msg}</div>}
      </div>
    );
  }


  window.MlResearchTopics = { TopicsBank };
})();
